/*
 * voice-labels/tool.js — the page: recorder, scanner, library, sticker, print.
 *
 * Layout (blade): «screen + dock» on the left with three modes, the inspector
 * on the right showing the sticker of the CURRENT label — the draft being
 * recorded, the label just scanned or the one picked in the list.
 *
 * Pure rules (the code on the sticker, language, expiry, merge, trimming,
 * auto-stop) live in core.js; the library in store.js. Shared primitives:
 * MicManager, CameraManager + __qrDecode.liveFrame (the live frame every
 * scanner takes), __audioGate (a label plays after the decode's await),
 * __speak (text labels), TakeLevel (quiet phone takes), MP3Encoder (one format
 * every device plays), label-maker sheets/layout/render/pdf (print), the
 * shared uploader (a photo of a sticker), __downloadFile / __batchDeliver.
 */
(function () {
    'use strict';

    var CFG = window.VL_CFG || {};
    var T = CFG.t || {};
    var LOC = CFG.locale || 'en';
    var SLUG = 'voice-labels';
    var MAX_S = 60;
    var BASE = String(CFG.pageUrl || (location.origin + location.pathname)).replace(/[?#].*$/, '');
    var LANG_TAG = { ru: 'ru-RU', uk: 'uk-UA', en: 'en-US', de: 'de-DE', es: 'es-MX', fr: 'fr-FR', pt: 'pt-BR', id: 'id-ID', ko: 'ko-KR', ar: 'ar-SA' };
    var FONT = "'VL Noto Sans','Noto Sans',system-ui,sans-serif";

    var C, Store, DK, CL = null;
    var el = {};

    /* ── small helpers ───────────────────────────────────────────────────── */
    function $(id) { return document.getElementById(id); }
    function t(k, vars) {
        var s = T[k];
        if (typeof s !== 'string') s = k;
        if (vars) Object.keys(vars).forEach(function (v) { s = s.split(':' + v).join(String(vars[v])); });
        return s;
    }
    function icon(n) { return DK ? DK.icon(n) : ''; }
    function intlLoc() { try { return window.__intlLocale ? window.__intlLocale() : LOC; } catch (e) { return LOC; } }
    function feat(f, extra) {
        try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, f: f }, extra || {})); } catch (e) { }
    }
    function stage(s) { try { if (window.__stage) window.__stage(s); } catch (e) { } }
    function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (e) { } }
    function announce(msg) { try { if (window.__announce) window.__announce(msg); } catch (e) { } }
    function handleError(err, where, say) {
        try {
            if (window.__handleError) { window.__handleError(err, where, { say: say }); return; }
        } catch (e) { }
        if (say) toast(say, 'error');
    }
    function setting(key, def) {
        try { if (window.__toolSettings) { var v = window.__toolSettings.load(SLUG, key, def); return v == null ? def : v; } } catch (e) { }
        return def;
    }
    function saveSetting(key, val) { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, key, val); } catch (e) { } }
    function setText(node, s) { if (node && node.textContent !== s) node.textContent = s; }
    function show(node, on) { if (node) node.hidden = !on; }
    function langTag(l) { return LANG_TAG[l] || l || LANG_TAG[LOC] || 'en-US'; }
    function fmtDate(ymd, opts) {
        var d = ymd instanceof Date ? ymd : (function () { var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || ''); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null; })();
        if (!d) return '';
        var o = Object.assign({ day: 'numeric', month: 'long' }, opts || {});
        if (d.getFullYear() !== new Date().getFullYear()) o.year = 'numeric';
        try { return new Intl.DateTimeFormat(intlLoc(), o).format(d); } catch (e) { return d.toLocaleDateString(); }
    }
    function relDays(days) {
        try {
            var f = new Intl.RelativeTimeFormat(intlLoc(), { numeric: 'auto' });
            if (Math.abs(days) >= 75) return f.format(Math.round(days / 30.4), 'month');
            return f.format(days, 'day');
        } catch (e) { return String(days); }
    }
    function labelName(row) {
        if (!row) return '';
        if (row.text) return row.text;
        return t('untitled', { date: fmtDate(new Date(row.createdAt || Date.now()), { month: 'short' }) });
    }
    function urlOf(row) { return C.buildUrl(BASE, row); }
    function fileBase(s, dflt) {
        var n = String(s || '');
        try { if (window.__outputName && window.__outputName.sanitize) n = window.__outputName.sanitize(n); else n = n.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').trim().slice(0, 80); } catch (e) { }
        return n || dflt;
    }

    /* ── state ───────────────────────────────────────────────────────────── */
    var S = {
        mode: 'create',
        labels: [],
        byId: {},
        current: null,          // { row, here:bool } shown in the inspector outside «create»
        draft: null,            // see newDraft()
        rec: 'idle',
        pick: false, picked: {}, query: '',
        opts: { cont: true, sound: true, vib: true, sayExp: true, rate: 1, autostop: true },
        landing: null,          // a label opened from its sticker link
        armedDelete: 0,
        wide: false,            // three columns: the list is always on screen
        pinCurrent: false,      // in «create», a label picked in the list wins over the draft
    };
    function newDraft(from) {
        return {
            id: from ? from.id : C.newId(),
            editingId: from ? from.id : null,
            text: from ? (from.text || '') : '',
            expires: from ? (from.expires || '') : '',
            take: from && from.audio ? { bytes: from.audio, mime: from.mime, duration: from.duration, peaks: from.peaks || null, kept: true } : null,
            createdAt: from ? from.createdAt : 0,
            dirty: false,
            saved: !!from,
        };
    }

    /* ════════════════════════════════════════════════════════════════════
       LIBRARY
       ════════════════════════════════════════════════════════════════════ */
    function reload() {
        return Store.all().then(function (rows) {
            S.labels = rows;
            S.byId = {};
            rows.forEach(function (r) { S.byId[r.id] = r; });
            setText(el.count, String(rows.length));
            setText(el.count2, String(rows.length));
            renderList();
            renderBackupSummary();
            return rows;
        }).catch(function (err) {
            handleError(err, 'vl_store_read', t('store_failed'));
            return [];
        });
    }

    function miniWave(peaks) {
        if (!peaks || !peaks.length) return '';
        var bars = 18, w = 56, h = 22, bw = w / bars, out = '';
        for (var i = 0; i < bars; i++) {
            var s = Math.floor(i * peaks.length / bars), e = Math.max(s + 1, Math.floor((i + 1) * peaks.length / bars)), m = 0;
            for (var k = s; k < e; k++) m = Math.max(m, peaks[k] || 0);
            var bh = Math.max(2, (m / 255) * (h - 2));
            out += '<rect x="' + (i * bw + 0.6).toFixed(1) + '" y="' + ((h - bh) / 2).toFixed(1) + '" width="' + (bw - 1.4).toFixed(1) + '" height="' + bh.toFixed(1) + '" rx="1"/>';
        }
        return '<svg class="vl-mini" viewBox="0 0 ' + w + ' ' + h + '" fill="currentColor" aria-hidden="true">' + out + '</svg>';
    }
    function escHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

    function renderList() {
        var q = S.query.trim().toLowerCase();
        var rows = S.labels.filter(function (r) { return !q || labelName(r).toLowerCase().indexOf(q) >= 0; });
        var any = S.labels.length > 0;
        show(el.list, rows.length > 0);
        show(el.empty, rows.length === 0);
        setText(el.emptyT, any ? t('lib_nothing') : t('lib_empty_t'));
        setText(el.emptyP, any ? t('lib_nothing_p') : t('lib_empty_p'));
        show(el.emptyGo, !any);
        el.printAll.disabled = !any;
        el.pickMode.disabled = !any;
        var cur = S.current && S.current.row ? S.current.row.id : null;
        var html = '';
        rows.forEach(function (r) {
            var exp = C.expiryInfo(r.expires);
            var meta = [];
            if (r.audio) meta.push(C.fmtDuration(r.duration));
            else meta.push(escHtml(t('lib_text_only')));
            if (exp) {
                var cls = exp.state === 'expired' ? 'vl-dot bad' : (exp.state === 'ok' ? '' : 'vl-dot');
                meta.push((cls ? '<i class="' + cls + '"></i>' : '') + escHtml(exp.state === 'expired' ? t('lib_expired') : t('lib_until', { date: fmtDate(exp.date, { month: 'short' }) })));
            } else {
                meta.push(escHtml(fmtDate(new Date(r.updatedAt || r.createdAt || Date.now()), { month: 'short' })));
            }
            var name = escHtml(labelName(r));
            html += '<div class="vl-row" role="listitem" data-id="' + escHtml(r.id) + '"' + (r.id === cur ? ' aria-current="true"' : '') + '>'
                + '<button type="button" class="vl-row-play' + (r.audio ? '' : ' is-text') + '" data-act="play" aria-label="' + escHtml(t('act_play')) + ': ' + name + '">' + icon('play') + '</button>'
                + '<button type="button" class="vl-row-b" data-act="pick"><span class="vl-row-t">' + name + '</span><span class="vl-row-m">' + meta.join(' · ') + '</span></button>'
                + (S.pick
                    ? '<input type="checkbox" class="vl-row-pick" data-act="check" aria-label="' + name + '"' + (S.picked[r.id] ? ' checked' : '') + '>'
                    : (r.peaks ? miniWave(r.peaks) : ''))
                + '</div>';
        });
        el.list.innerHTML = html;
        updatePickInfo();
    }
    function updatePickInfo() {
        var n = Object.keys(S.picked).filter(function (k) { return S.picked[k] && S.byId[k]; }).length;
        show(el.pickFoot, S.pick);
        setText(el.pickInfo, t('lib_picked', { n: n }));
        setText(el.printAllLbl, S.pick ? t('lib_print_sel', { n: n }) : t('lib_print_all'));
        el.printAll.disabled = S.pick ? n === 0 : S.labels.length === 0;
        el.pickMode.setAttribute('aria-pressed', S.pick ? 'true' : 'false');
        el.pickMode.classList.toggle('is-on', S.pick);
    }

    function onListClick(e) {
        var row = e.target.closest('.vl-row');
        if (!row) return;
        var id = row.getAttribute('data-id');
        var r = S.byId[id];
        if (!r) return;
        var act = (e.target.closest('[data-act]') || {}).getAttribute ? e.target.closest('[data-act]').getAttribute('data-act') : 'pick';
        if (act === 'check') { S.picked[id] = e.target.checked; updatePickInfo(); return; }
        if (S.pick && act !== 'play') {
            S.picked[id] = !S.picked[id];
            var box = row.querySelector('.vl-row-pick');
            if (box) box.checked = !!S.picked[id];
            updatePickInfo();
            return;
        }
        S.pinCurrent = true;
        setCurrent(r, true);
        if (act === 'play') playLabel(r, null, { via: 'list' });
    }

    /* ════════════════════════════════════════════════════════════════════
       MODES
       ════════════════════════════════════════════════════════════════════ */
    function setMode(m, o) {
        o = o || {};
        if (m === 'library' && S.wide) m = 'create';
        if (m === S.mode && !o.force) return;
        if (S.mode === 'scan' && m !== 'scan') stopScan();
        if (S.mode === 'create' && m !== 'create' && S.rec === 'recording') stopRec('mode');
        S.mode = m;
        el.root.setAttribute('data-mode', m);
        el.tabs.forEach(function (b) {
            var on = b.getAttribute('data-mode') === m;
            b.setAttribute('aria-selected', on ? 'true' : 'false');
            b.tabIndex = on ? 0 : -1;
        });
        show(el.paneCreate, m === 'create');
        show(el.paneScan, m === 'scan');
        show(el.paneLib, m === 'library');
        if (!o.keep) saveSetting('mode', m);
        if (m === 'library' && !(S.current && S.byId[S.current.row.id]) && S.labels.length) S.current = { row: S.labels[0], here: true };
        if (m !== 'create') S.pinCurrent = false;
        renderSide();
        if (m === 'library' || S.wide) renderList();
        if (m === 'create') requestAnimationFrame(drawWave);
        try { if (DK) DK.fitDock(m === 'create' ? el.recDock : el.scanDock); } catch (e) { }
    }

    /* ════════════════════════════════════════════════════════════════════
       RECORDER
       ════════════════════════════════════════════════════════════════════ */
    var R = { mic: null, stream: null, mr: null, chunks: [], ctx: null, an: null, buf: null, t0: 0, levels: [], tick: 0, stopper: null, mime: '', why: '', dur: 0 };

    function pickMime() {
        if (!window.MediaRecorder || !MediaRecorder.isTypeSupported) return '';
        var c = ['audio/webm;codecs=opus', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus'];
        for (var i = 0; i < c.length; i++) { try { if (MediaRecorder.isTypeSupported(c[i])) return c[i]; } catch (e) { } }
        return '';
    }

    function setRec(state) {
        S.rec = state;
        el.root.setAttribute('data-rec', state);
        var d = S.draft;
        var pill = el.recPill;
        var map = { idle: ['', 'rec_ready'], starting: ['wait', 'rec_starting'], recording: ['stop', 'rec_on'], processing: ['wait', 'rec_proc'], take: ['run', 'rec_take'], saved: ['done', 'rec_saved'] };
        var m = map[state] || map.idle;
        if (state === 'idle' && d && d.editingId) m = ['done', 'rec_editing'];
        if (m[0]) pill.setAttribute('data-state', m[0]); else pill.removeAttribute('data-state');
        setText(pill, t(m[1]));

        var main = el.main, lbl = el.mainLbl;
        main.classList.remove('dk-primary', 'dk-stop', 'dk-go');
        main.disabled = false;
        var mainIcon = 'mic', mainText = t('btn_record'), cls = 'dk-primary';
        var hasTake = !!(d && d.take);
        var hasText = !!(d && C.cleanText(d.text));
        if (state === 'starting') { main.disabled = true; mainText = t('rec_starting'); }
        else if (state === 'recording') { mainIcon = 'stop'; mainText = t('btn_stop'); cls = 'dk-stop'; }
        else if (state === 'processing') { main.disabled = true; mainText = t('rec_proc'); }
        else if (state === 'take' || (d && d.editingId && d.dirty)) { mainIcon = 'check'; mainText = d && d.editingId ? t('btn_save_changes') : t('btn_save'); cls = 'dk-go'; }
        else if (state === 'saved') { mainIcon = 'plus'; mainText = t('btn_new'); cls = 'dk-primary'; }
        main.classList.add(cls);
        var svg = main.querySelector('svg');
        if (svg && DK) { var tmp = document.createElement('span'); tmp.innerHTML = icon(mainIcon); svg.replaceWith(tmp.firstChild); }
        setText(lbl, mainText);

        var idleish = state === 'idle' || state === 'take' || state === 'saved';
        el.root.setAttribute('data-take', hasTake ? '1' : '0');
        show(el.play, hasTake && idleish);
        show(el.redo, hasTake && (state === 'take' || state === 'saved'));
        show(el.saveText, idleish && !hasTake && hasText && !(d && d.editingId));
        show(el.neuralWrap, idleish && !hasTake && hasText && neuralVoices(C.guessLang(d.text, LOC)).length > 0);
        show(el.cancelEdit, !!(d && d.editingId && d.dirty && idleish));
        el.text.disabled = state === 'processing';

        var hint = 'rec_hint_idle';
        if (state === 'recording') hint = S.opts.autostop ? 'rec_hint_on' : 'rec_hint_on_manual';
        else if (state === 'processing') hint = 'rec_hint_proc';
        else if (state === 'take') hint = 'rec_hint_take';
        else if (state === 'saved') hint = 'rec_hint_saved';
        else if (d && d.editingId) hint = 'rec_hint_edit';
        setHint(t(hint));
        if (!(state === 'recording' || state === 'processing')) setText(el.recTime, C.fmtDuration(hasTake ? d.take.duration : 0));
        drawWave();
        renderSide();
    }
    function setHint(s, warn) {
        setText(el.recHint, s);
        el.recHint.classList.toggle('warn', !!warn);
    }

    function onMain() {
        var st = S.rec, d = S.draft;
        if (st === 'recording') { stopRec('button'); return; }
        if (st === 'starting' || st === 'processing') return;
        if (st === 'take' || (d && d.editingId && d.dirty)) { saveDraft(); return; }
        if (st === 'saved') { startNew(); return; }
        startRec();
    }

    function startNew() {
        S.pinCurrent = false;
        stopPlayback();
        S.draft = newDraft(null);
        el.text.value = '';
        updateTextCount();
        disarmUnsaved();
        setRec('idle');
        try { el.main.focus({ preventScroll: true }); } catch (e) { }
    }

    function startRec() {
        S.pinCurrent = false;
        if (!window.MediaRecorder) { setHint(t('rec_unsupported'), true); feat('rec_unsupported'); return; }
        stopPlayback();
        unlockAudio();
        try { R.ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch (e) { R.ctx = null; }
        setRec('starting');
        var started = Date.now();
        Promise.resolve().then(function () {
            if (!window.MicManager) throw new Error('mic_manager_missing');
            if (!R.mic) R.mic = new window.MicManager({ slug: SLUG });
            return R.mic.start();
        }).then(function (stream) {
            R.stream = stream;
            R.mime = pickMime();
            try {
                R.mr = R.mime ? new MediaRecorder(stream, { mimeType: R.mime, audioBitsPerSecond: 96000 }) : new MediaRecorder(stream);
            } catch (e) { R.mr = new MediaRecorder(stream); }
            R.chunks = [];
            R.mr.ondataavailable = function (ev) { if (ev.data && ev.data.size) R.chunks.push(ev.data); };
            R.mr.onstop = onRecStop;
            R.mr.start(250);
            if (R.ctx) {
                try { if (R.ctx.state === 'suspended') R.ctx.resume(); } catch (e) { }
                try {
                    var src = R.ctx.createMediaStreamSource(stream);
                    R.an = R.ctx.createAnalyser();
                    R.an.fftSize = 1024;
                    src.connect(R.an);
                    R.buf = new Float32Array(R.an.fftSize);
                } catch (e) { R.an = null; }
            }
            R.t0 = performance.now();
            R.levels = [];
            R.stopper = C.silenceStopper({ holdMs: 2000 });
            R.tick = setInterval(sampleLevel, 50);
            setRec('recording');
            loopDraw();
            feat('record_start', { ms: Date.now() - started, mime: R.mime || 'default' });
            stage('recording');
        }).catch(function (err) {
            closeRecCtx();
            setRec(S.draft && S.draft.take ? 'take' : 'idle');
            var msg = (err && err.code && R.mic && R.mic.getErrorMessage) ? R.mic.getErrorMessage(err.code) : t('mic_error');
            setHint(msg, true);
            feat('mic_failed', { code: String((err && (err.code || err.name)) || 'unknown').slice(0, 40) });
        });
    }

    function sampleLevel() {
        if (S.rec !== 'recording') return;
        var el2 = (performance.now() - R.t0) / 1000;
        var db = -120;
        if (R.an && R.buf) {
            try {
                R.an.getFloatTimeDomainData(R.buf);
                var s = 0;
                for (var i = 0; i < R.buf.length; i++) s += R.buf[i] * R.buf[i];
                var rms = Math.sqrt(s / R.buf.length);
                db = rms > 1e-6 ? 20 * Math.log10(rms) : -120;
            } catch (e) { }
        }
        R.levels.push(db);
        setText(el.recTime, C.fmtDuration(el2));
        if (el2 >= MAX_S) { stopRec('max'); return; }
        if (S.opts.autostop && R.an && R.stopper.feed(db, el2 * 1000) === 'stop') stopRec('silence');
    }

    function stopRec(why) {
        if (S.rec !== 'recording') return;
        clearInterval(R.tick); R.tick = 0;
        R.why = why;
        R.dur = (performance.now() - R.t0) / 1000;
        setRec('processing');
        try { R.mr.stop(); } catch (e) { onRecStop(); }
    }

    function closeRecCtx() {
        try { if (R.mic) R.mic.stop(); } catch (e) { }
        R.stream = null;
        if (R.ctx) { var c = R.ctx; R.ctx = null; R.an = null; try { c.close(); } catch (e) { } }
    }

    function onRecStop() {
        try { if (R.mic) R.mic.stop(); } catch (e) { }
        var type = (R.mr && R.mr.mimeType) || R.mime || 'audio/webm';
        var blob = new Blob(R.chunks, { type: type });
        R.chunks = [];
        var dur = R.dur;
        var levels = R.levels.slice();
        /* An empty take (Stop pressed at once, a muted input, a recorder that
           never delivered) is «no voice heard» now — not a decode. Measured on
           prod 23.10: a 0-byte webm sent to decodeAudioData never settled,
           because the site-wide decode door retries a failed decode through
           FFmpeg (a 30 MB engine), and «processing» hung for good. */
        if (blob.size < 512) {
            closeRecCtx();
            setRec(S.draft.take ? 'take' : 'idle');
            setHint(t('rec_silent'), true);
            announce(t('rec_silent'));
            feat('take_empty', { size: blob.size, s: Math.round(dur * 10) / 10 });
            return;
        }
        bounded(processTake(blob, dur), 20000, 'vl_take').catch(function (err) {
            feat('take_process_failed', { err: String((err && (err.code || err.message)) || err).slice(0, 80) });
            return blob.arrayBuffer().then(function (ab) {
                return { bytes: ab, mime: type.split(';')[0], duration: dur, peaks: levelsToPeaks(levels), raw: true };
            });
        }).then(function (take) {
            closeRecCtx();
            if (!take || take.silent || !(take.bytes && take.bytes.byteLength)) {
                setRec(S.draft.take ? 'take' : 'idle');
                setHint(t('rec_silent'), true);
                announce(t('rec_silent'));
                feat('take_silent');
                return;
            }
            S.draft.take = take;
            S.draft.dirty = true;
            armUnsaved();
            setRec('take');
            stage('take_ready');
            announce(t('rec_take_said', { dur: C.fmtDuration(take.duration) }));
            feat('take_ready', { s: Math.round(take.duration), why: R.why, boost: take.boostDb || 0, raw: take.raw ? 1 : 0 });
        });
    }

    function levelsToPeaks(levels) {
        var bars = 48, out = [];
        for (var i = 0; i < bars; i++) {
            var s = Math.floor(i * levels.length / bars), e = Math.max(s + 1, Math.floor((i + 1) * levels.length / bars)), m = -120;
            for (var k = s; k < e && k < levels.length; k++) m = Math.max(m, levels[k]);
            out.push(Math.max(0, Math.min(255, Math.round((m + 60) / 60 * 255))));
        }
        return out;
    }

    /* The take as a label: voice trimmed to itself, a quiet phone take raised
       (TakeLevel: iOS records 20–30 dB under desktop), then MP3 — one format
       every phone plays, at a sixth of the WAV size. Any step that fails keeps
       the original recording instead: a label is never lost to processing. */
    /* A wait that always ends: the shared __bounded (WAIT_TIMEOUT) where the
       core has it, the same race otherwise. */
    function bounded(p, ms, phase) {
        if (typeof window.__bounded === 'function') return window.__bounded(p, ms, phase);
        return Promise.race([p, new Promise(function (_, rej) { setTimeout(function () { var e = new Error('WAIT_TIMEOUT'); e.code = 'WAIT_TIMEOUT'; e.phase = phase; rej(e); }, ms); })]);
    }

    /* Our own recording only: a decode that fails here has nothing for the
       site's FFmpeg retry to rescue (the bytes are the browser's own webm/mp4),
       and the original take is kept anyway. So the retry is switched off for
       exactly this decode (window.__suppressDecodeFallback, read by the core
       when the native decode rejects) and restored when it settles. */
    function decodeOwn(ctx, ab) {
        var prev = window.__suppressDecodeFallback;
        window.__suppressDecodeFallback = true;
        var restore = function () { window.__suppressDecodeFallback = prev; };
        var p = new Promise(function (resolve, reject) {
            var r = ctx.decodeAudioData(ab.slice(0), resolve, reject);
            if (r && r.then) r.then(resolve, reject);
        });
        p.then(restore, restore);
        return p;
    }

    function processTake(blob, dur) {
        return blob.arrayBuffer().then(function (ab) {
            var ctx = R.ctx;
            if (!ctx) throw new Error('no_ctx');
            return decodeOwn(ctx, ab).then(function (audio) { return { audio: audio, ab: ab }; });
        }).then(function (o) {
            var a = o.audio, n = a.length, sr = a.sampleRate, ch = a.numberOfChannels;
            var mono = new Float32Array(n);
            for (var c = 0; c < ch; c++) { var d = a.getChannelData(c); for (var i = 0; i < n; i++) mono[i] += d[i] / ch; }
            var b = C.trimBounds(mono, sr);
            var seg = mono.slice(b.start, b.end);
            var stats = window.TakeLevel ? window.TakeLevel.analyze({ channels: [seg], sampleRate: sr }) : null;
            if (stats && stats.levelDb < -58) return { silent: true };
            var boost = stats ? window.TakeLevel.normalizeGainDb(stats, { targetDb: -6 }) : 0;
            if (boost) window.TakeLevel.applyGain({ channels: [seg], sampleRate: sr }, boost);
            var p = C.peaks(seg, 48), mx = 0;
            p.forEach(function (v) { if (v > mx) mx = v; });
            var peaks = p.map(function (v) { return mx > 0 ? Math.round(v / mx * 255) : 0; });
            var duration = seg.length / sr;
            if (!window.MP3Encoder) throw new Error('no_mp3');
            var buf = R.ctx.createBuffer(1, Math.max(1, seg.length), sr);
            buf.getChannelData(0).set(seg);
            return window.MP3Encoder.encode(buf, { bitrate: 64, slug: SLUG, reportSuccess: false }).then(function (mp3) {
                return mp3.arrayBuffer();
            }).then(function (bytes) {
                return { bytes: bytes, mime: 'audio/mpeg', duration: duration, peaks: peaks, boostDb: boost };
            });
        });
    }

    /* ── neural voice for a caption (server TTS, the /text-to-speech lane) ── */
    function neuralVoices(lang) {
        var v = (CFG.voices || {})[lang];
        return Array.isArray(v) ? v : [];
    }
    function neuralMsg(msg) {
        setText(el.neuralMsgT, msg || '');
        show(el.neuralMsg, !!msg);
    }
    function renderNeuralVoices() {
        var d = S.draft, lang = C.guessLang(d.text, LOC), list = neuralVoices(lang);
        var pick = setting('voice_' + lang, list.length ? list[0].id : '');
        if (!list.some(function (v) { return v.id === pick; }) && list.length) pick = list[0].id;
        el.neuralVoices.innerHTML = list.map(function (v) {
            var on = v.id === pick;
            return '<button type="button" class="dk-chip" role="radio" aria-checked="' + (on ? 'true' : 'false') + '" aria-pressed="' + (on ? 'true' : 'false') + '" data-voice="' + escHtml(v.id) + '">' + escHtml(v.label) + (v.g ? ' <span aria-hidden="true">' + (v.g === 'F' ? '♀' : '♂') + '</span>' : '') + '</button>';
        }).join('');
        neuralMsg('');
    }
    function pickedVoice() {
        var b = el.neuralVoices.querySelector('[aria-checked="true"]');
        return b ? b.getAttribute('data-voice') : '';
    }
    function decodeForTake(ab) {
        var ctx;
        try { ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 1, 44100); } catch (e) { ctx = null; }
        if (!ctx) return Promise.resolve(null);
        return decodeOwn(ctx, ab).then(function (a) {
            var ch = a.getChannelData(0), p = C.peaks(ch, 48), mx = 0;
            p.forEach(function (v) { if (v > mx) mx = v; });
            return { duration: a.duration, peaks: p.map(function (v) { return mx > 0 ? Math.round(v / mx * 255) : 0; }) };
        }, function () { return null; });
    }
    function onNeuralGo() {
        var d = S.draft, text = C.cleanText(d.text), lang = C.guessLang(text, LOC), voice = pickedVoice();
        if (!text || !voice || S.neuralBusy) return;
        S.neuralBusy = true;
        el.neuralGo.disabled = true;
        setText(el.neuralGoLbl, t('neural_busy'));
        neuralMsg('');
        saveSetting('voice_' + lang, voice);
        var csrf = (document.querySelector('meta[name="csrf-token"]') || {}).content || '';
        var started = Date.now();
        var done = function () { S.neuralBusy = false; el.neuralGo.disabled = false; setText(el.neuralGoLbl, t('neural_go')); };
        bounded(fetch('/api/tts/synthesize', {
            method: 'POST', credentials: 'same-origin', redirect: 'error',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json, audio/mpeg', 'X-CSRF-TOKEN': csrf, 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ text: text, language: lang, voice: voice, format: 'mp3', locale: LOC }),
        }), 45000, 'vl_neural').then(function (res) {
            var ct = res.headers.get('Content-Type') || '';
            if (res.ok && /audio/.test(ct)) return res.arrayBuffer().then(function (ab) { return { ab: ab }; });
            return res.json().catch(function () { return {}; }).then(function (b) { return { status: res.status, body: b || {} }; });
        }).then(function (r) {
            if (r.ab) {
                return decodeForTake(r.ab).then(function (info) {
                    done();
                    d.take = { bytes: r.ab, mime: 'audio/mpeg', duration: info ? info.duration : 0, peaks: info ? info.peaks : null, neural: voice };
                    d.dirty = true;
                    armUnsaved();
                    try { if (window.__anchoredPopover) window.__anchoredPopover.close(el.neuralPop); else el.neuralPop.hidePopover(); } catch (e) { }
                    setRec('take');
                    var v = neuralVoices(lang).filter(function (x) { return x.id === voice; })[0];
                    announce(t('neural_done', { voice: v ? v.label : voice }));
                    feat('neural_voice', { ok: 1, voice: voice, lang: lang, ms: Date.now() - started });
                });
            }
            done();
            var b = r.body || {}, msg;
            if (r.status === 429 && b.error === 'daily_quota') msg = (typeof b.message === 'string' && b.message) ? b.message : t('neural_quota');
            else if (r.status === 429) msg = t('neural_rate');
            else if (r.status === 403) msg = t('neural_pro', { plan: CFG.plan || '' });
            else msg = t('neural_fail', { err: b.error || ('http ' + r.status) });
            neuralMsg(msg);
            feat('neural_voice', { ok: 0, voice: voice, lang: lang, status: r.status, err: String(b.error || '').slice(0, 40) });
        }).catch(function (err) {
            done();
            neuralMsg(t('neural_fail', { err: (err && err.code === 'WAIT_TIMEOUT') ? t('neural_timeout') : t('cloud_err_net') }));
            feat('neural_voice', { ok: 0, voice: voice, lang: lang, err: String((err && (err.code || err.name)) || err).slice(0, 40) });
        });
    }

    function onText() {
        S.pinCurrent = false;
        var d = S.draft;
        d.text = el.text.value;
        if (d.saved || d.editingId) d.dirty = true;
        updateTextCount();
        if (C.cleanText(d.text) || d.take) armUnsaved();
        setRec(S.rec === 'saved' && d.dirty ? 'idle' : S.rec);
    }
    function updateTextCount() {
        var n = Array.from(el.text.value || '').length;
        setText(el.textCount, n + '/' + C.TEXT_MAX);
    }

    function saveDraft() {
        var d = S.draft;
        var text = C.cleanText(d.text);
        if (!d.take && !text) { setHint(t('save_need'), true); return Promise.resolve(); }
        var now = Date.now();
        var prev = d.editingId ? S.byId[d.editingId] : null;
        var row = {
            id: d.id,
            audio: d.take ? d.take.bytes : null,
            mime: d.take ? d.take.mime : '',
            text: text,
            lang: text ? C.guessLang(text, LOC) : '',
            duration: d.take ? d.take.duration : 0,
            peaks: d.take ? d.take.peaks : null,
            expires: d.expires || '',
            createdAt: prev ? prev.createdAt : (d.createdAt || now),
            updatedAt: now,
            // The voice changed only with a new take; a caption edit keeps it.
            audioAt: (d.take && !d.take.kept) ? now : (prev ? prev.audioAt || 0 : 0),
            cloudVersion: prev ? prev.cloudVersion || 0 : 0,
            cloudSyncedAt: prev ? prev.cloudSyncedAt || 0 : 0,
        };
        var wasNew = !prev;
        return Store.put(row).then(function (saved) {
            d.saved = true; d.dirty = false; d.editingId = saved.id; d.createdAt = saved.createdAt;
            if (d.take) d.take.kept = true;
            disarmUnsaved();
            return reload().then(function () {
                S.current = { row: S.byId[saved.id] || saved, here: true };
                setRec('saved');
                announce(t(wasNew ? 'saved_said' : 'updated_said', { name: labelName(saved) }));
                if (wasNew) {
                    askPersist();
                    stage('saved');
                    try {
                        if (window.__aeToolComplete) window.__aeToolComplete({ kind: saved.audio ? 'voice' : 'text', s: Math.round(saved.duration), txt: saved.text ? 1 : 0, exp: saved.expires ? 1 : 0 });
                    } catch (e) { }
                }
                feat(wasNew ? 'label_saved' : 'label_updated', { voice: saved.audio ? 1 : 0, text: saved.text ? 1 : 0 });
                cloudAfterChange();
            });
        }).catch(function (err) {
            handleError(err, 'vl_store_write', t('store_failed'));
        });
    }

    function editLabel(row) {
        if (!row) return;
        stopPlayback();
        S.draft = newDraft(row);
        S.pinCurrent = false;
        el.text.value = row.text || '';
        updateTextCount();
        setMode('create');
        setRec('idle');
        try { el.text.focus({ preventScroll: true }); } catch (e) { }
        feat('label_edit');
    }
    function cancelEdit() {
        var id = S.draft && S.draft.editingId;
        var row = id ? S.byId[id] : null;
        if (row) { S.draft = newDraft(row); el.text.value = row.text || ''; updateTextCount(); disarmUnsaved(); setRec('saved'); }
        else startNew();
    }
    function redo() {
        stopPlayback();
        S.draft.take = null;
        S.draft.dirty = !!S.draft.editingId;
        setRec('idle');
        startRec();
    }

    function armUnsaved() { try { if (window.__unsavedWork) window.__unsavedWork.arm('voice-labels:draft'); } catch (e) { } }
    function disarmUnsaved() { try { if (window.__unsavedWork) window.__unsavedWork.disarm(); } catch (e) { } }

    /* ── the screen's voice line ─────────────────────────────────────────── */
    var W = { raf: 0, w: 0, h: 0, dpr: 1 };
    function sizeWave() {
        var c = el.wave, r = c.getBoundingClientRect();
        var dpr = Math.min(2, window.devicePixelRatio || 1);
        var w = Math.max(1, Math.round(r.width * dpr)), h = Math.max(1, Math.round(r.height * dpr));
        if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
        W.w = w; W.h = h; W.dpr = dpr;
    }
    function loopDraw() {
        if (W.raf) return;
        var step = function () {
            W.raf = 0;
            drawWave();
            if (S.rec === 'recording' || player.previewing) W.raf = requestAnimationFrame(step);
        };
        W.raf = requestAnimationFrame(step);
    }
    function drawWave() {
        var c = el.wave;
        if (!c || S.mode !== 'create') return;
        sizeWave();
        var g = c.getContext('2d'), w = W.w, h = W.h, dpr = W.dpr;
        g.clearRect(0, 0, w, h);
        var mid = h / 2, bw = 3 * dpr, gap = 2 * dpr, pitch = bw + gap;
        var d = S.draft;
        if (S.rec === 'recording' || S.rec === 'processing') {
            var lv = R.levels, n = Math.floor(w / pitch), start = Math.max(0, lv.length - n);
            var grad = g.createLinearGradient(0, 0, 0, h);
            grad.addColorStop(0, '#ff8a7a'); grad.addColorStop(.5, '#ff5d5d'); grad.addColorStop(1, '#ff8a7a');
            g.fillStyle = S.rec === 'processing' ? 'rgba(160,170,190,.55)' : grad;
            for (var i = start; i < lv.length; i++) {
                var amp = Math.max(0, Math.min(1, (lv[i] + 60) / 60));
                var bh = Math.max(2 * dpr, amp * (h - 8 * dpr));
                var x = (i - start) * pitch;
                g.fillRect(x, mid - bh / 2, bw, bh);
            }
            return;
        }
        if (d && d.take && d.take.peaks) {
            var pk = d.take.peaks, bars = Math.max(12, Math.floor(w / pitch));
            var played = player.previewing && player.el && player.el.duration > 0 && isFinite(player.el.duration) ? player.el.currentTime / player.el.duration : -1;
            for (var b = 0; b < bars; b++) {
                var s = Math.floor(b * pk.length / bars), e = Math.max(s + 1, Math.floor((b + 1) * pk.length / bars)), m = 0;
                for (var k = s; k < e; k++) m = Math.max(m, pk[k] || 0);
                var bh2 = Math.max(2 * dpr, (m / 255) * (h - 10 * dpr));
                var on = played >= 0 && b / bars <= played;
                g.fillStyle = on ? '#9cc0ff' : (S.rec === 'saved' ? 'rgba(52,211,153,.75)' : 'rgba(122,167,255,.72)');
                g.fillRect(b * pitch + (w - bars * pitch) / 2, mid - bh2 / 2, bw, bh2);
            }
            return;
        }
        g.strokeStyle = 'rgba(255,255,255,.12)';
        g.lineWidth = dpr;
        g.setLineDash([3 * dpr, 4 * dpr]);
        g.beginPath(); g.moveTo(0, mid); g.lineTo(w, mid); g.stroke();
        g.setLineDash([]);
    }

    /* ════════════════════════════════════════════════════════════════════
       PLAYBACK — voice, text, expiry
       ════════════════════════════════════════════════════════════════════ */
    var player = { el: null, url: null, previewing: false, id: null, token: 0 };
    function audioEl() {
        if (!player.el) player.el = (window.__audioGate && window.__audioGate.element()) || new Audio();
        return player.el;
    }
    function unlockAudio() {
        try { if (window.__audioGate) window.__audioGate.unlockNow(audioEl()); } catch (e) { }
        primeSpeech();
    }
    var speechPrimed = false;
    function primeSpeech() {
        if (speechPrimed) return;
        try {
            if (!window.speechSynthesis || typeof SpeechSynthesisUtterance !== 'function') return;
            var u = new SpeechSynthesisUtterance(' ');
            u.volume = 0;
            window.speechSynthesis.speak(u);
            speechPrimed = true;
        } catch (e) { }
    }
    function stopPlayback() {
        player.token++;
        var a = player.el;
        if (a) { try { a.pause(); } catch (e) { } a.onended = null; a.onerror = null; }
        if (player.url) { try { URL.revokeObjectURL(player.url); } catch (e) { } player.url = null; }
        try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch (e) { }
        if (player.previewing) { player.previewing = false; setText(el.playLbl, t('btn_play')); drawWave(); }
        player.id = null;
    }
    function playBytes(bytes, mime, onEnd) {
        var a = audioEl(), tok = player.token;
        player.url = URL.createObjectURL(new Blob([bytes], { type: mime || 'audio/mpeg' }));
        a.onended = function () { if (tok === player.token && onEnd) onEnd(); };
        a.onerror = function () { if (tok === player.token && onEnd) onEnd('error'); };
        var p = window.__audioGate ? window.__audioGate.play(a, player.url) : (a.src = player.url, a.play());
        return Promise.resolve(p);
    }
    function speak(text, lang, onEnd) {
        var tok = player.token;
        if (!text) { if (onEnd) onEnd(); return false; }
        if (typeof window.__speak !== 'function') { if (onEnd) onEnd('no_tts'); return false; }
        var u = window.__speak(text, {
            lang: langTag(lang), rate: S.opts.rate,
            onend: function () { if (tok === player.token && onEnd) onEnd(); },
            onerror: function (e) { if (tok === player.token && onEnd) onEnd((e && e.error) || 'error'); },
        });
        return !!u;
    }
    function expiryPhrase(expires) {
        var info = C.expiryInfo(expires);
        if (!info) return '';
        if (info.state === 'expired') return t('say_exp_expired', { rel: relDays(info.days) });
        if (info.state === 'today') return t('say_exp_today');
        return t('say_exp_ok', { date: fmtDate(info.date), rel: relDays(info.days) });
    }

    /* Play a label: its voice; else its text through speech synthesis; then,
       when asked, how long it keeps. `parsed` is what the sticker said — the
       text in the code speaks on a phone that has no recording. */
    function playLabel(row, parsed, o) {
        o = o || {};
        stopPlayback();
        var text = row ? row.text : (parsed ? parsed.text : '');
        var lang = (row && row.lang) || (parsed && parsed.lang) || C.guessLang(text, LOC);
        var expires = row ? row.expires : (parsed ? parsed.expires : '');
        var after = function () {
            var p = S.opts.sayExp ? expiryPhrase(expires) : '';
            if (p) speak(p, LOC);
        };
        if (row && !row.audio && row.voiceUrl) {
            /* A label from the owner's cloud (any phone, no copy moved): the
               voice plays from its versioned address. */
            var a = audioEl(), tok = player.token;
            a.onended = function () { if (tok === player.token) after(); };
            a.onerror = function () { if (tok === player.token && text) speak(text, lang, after); };
            var pp = window.__audioGate ? window.__audioGate.play(a, row.voiceUrl) : (a.src = row.voiceUrl, a.play());
            return Promise.resolve(pp).then(function () {
                feat('play', { via: o.via || 'ui', kind: 'cloud' });
                return true;
            }, function (err) {
                feat('play_refused', { via: o.via || 'ui', kind: 'cloud', err: String((err && err.name) || err).slice(0, 40) });
                if (text) speak(text, lang, after);
                return false;
            });
        }
        if (row && row.audio) {
            return playBytes(row.audio, row.mime, after).then(function () {
                feat('play', { via: o.via || 'ui', kind: 'voice' });
                return true;
            }, function (err) {
                feat('play_refused', { via: o.via || 'ui', err: String((err && err.name) || err).slice(0, 40) });
                return false;
            });
        }
        if (text) {
            var ok = speak(text, lang, after);
            feat('play', { via: o.via || 'ui', kind: 'text', tts: ok ? 1 : 0 });
            if (!ok) toast(t('tts_missing'), 'warning');
            return Promise.resolve(ok);
        }
        speak(t('say_not_here'), LOC);
        return Promise.resolve(false);
    }

    function togglePreview() {
        var d = S.draft;
        if (!d || !d.take) return;
        if (player.previewing) { stopPlayback(); return; }
        stopPlayback();
        unlockAudio();
        player.previewing = true;
        setText(el.playLbl, t('btn_pause'));
        playBytes(d.take.bytes, d.take.mime, function () { player.previewing = false; setText(el.playLbl, t('btn_play')); drawWave(); })
            .then(function () { loopDraw(); }, function () { player.previewing = false; setText(el.playLbl, t('btn_play')); });
    }

    /* ════════════════════════════════════════════════════════════════════
       INSPECTOR — the sticker of the current label
       ════════════════════════════════════════════════════════════════════ */
    function sideLabel() {
        if (S.mode === 'create' && !(S.pinCurrent && S.current)) {
            var d = S.draft;
            if (!d) return null;
            var text = C.cleanText(d.text);
            if (!d.take && !text && !d.editingId) return null;
            return {
                row: {
                    id: d.id, text: text, lang: text ? C.guessLang(text, LOC) : '', expires: d.expires,
                    audio: d.take ? d.take.bytes : null, mime: d.take ? d.take.mime : '',
                    duration: d.take ? d.take.duration : 0, createdAt: d.createdAt || Date.now(),
                },
                state: d.saved && !d.dirty ? 'saved' : (d.editingId ? 'edited' : 'draft'),
                here: true,
            };
        }
        if (!S.current) return null;
        var r = S.current.row;
        var live = r && S.byId[r.id];
        return { row: live || r, state: live ? 'saved' : (r && r.fromCloud ? 'cloud' : 'away'), here: !!live };
    }

    function drawQr(canvas, text, cssPx) {
        var gen = window.qrcode;
        if (typeof gen !== 'function' || !canvas) return 0;
        try {
            var q = gen(0, 'M');
            q.addData(text);
            q.make();
            var n = q.getModuleCount(), quiet = 2;
            var dpr = Math.min(3, window.devicePixelRatio || 1);
            var px = Math.round(cssPx * dpr);
            var cell = Math.max(1, Math.floor(px / (n + quiet * 2)));
            var size = cell * (n + quiet * 2);
            canvas.width = size; canvas.height = size;
            var g = canvas.getContext('2d');
            g.fillStyle = '#fff'; g.fillRect(0, 0, size, size);
            g.fillStyle = '#000';
            for (var r = 0; r < n; r++) for (var c = 0; c < n; c++) if (q.isDark(r, c)) g.fillRect((c + quiet) * cell, (r + quiet) * cell, cell, cell);
            return n;
        } catch (e) { return 0; }
    }
    function qrModules(text) {
        try { var q = window.qrcode(0, 'M'); q.addData(text); q.make(); return q.getModuleCount(); } catch (e) { return 0; }
    }
    var qrUrlCache = {};
    function qrDataUrl(text) {
        if (qrUrlCache[text]) return qrUrlCache[text];
        var cv = document.createElement('canvas');
        drawQr(cv, text, 240);
        try { qrUrlCache[text] = cv.toDataURL('image/png'); } catch (e) { qrUrlCache[text] = ''; }
        return qrUrlCache[text];
    }

    function renderSide() {
        var s = sideLabel();
        el.side.classList.toggle('is-empty', !s);
        show(el.stickerEmpty, !s);
        show(el.sticker, !!s);
        show(el.code, !!s);
        show(el.state, !!s);
        var saved = !!(s && s.state === 'saved');
        show(el.acts, !!s && (saved || s.state === 'away' || s.state === 'cloud'));
        show(el.cloudTag, !!(s && CL && s.state !== 'draft' && CL.inCloud(s.row.id)));
        if (!s) {
            setText(el.sideNote, '');
            show(el.sideNote, false);
            renderExpiry(null);
            return;
        }
        var r = s.row;
        var url = urlOf(r);
        var n = drawQr(el.qr, url, 104);
        el.qr.setAttribute('aria-label', t('qr_alt', { name: labelName(r) }));
        el.sticker.classList.toggle('is-draft', s.state === 'draft' || s.state === 'edited');
        var tx = r.text || '';
        setText(el.stickerT, tx || t('sticker_no_text'));
        el.stickerT.setAttribute('lang', r.lang || LOC);
        el.stickerT.classList.toggle('is-empty', !tx);
        show(el.stickerV, !!(r.audio || r.voiceUrl));
        setText(el.stickerDur, (r.audio || r.voiceUrl) ? C.fmtDuration(r.duration) : '');
        setText(el.code, n ? t('code_info', { n: n, mm: C.minPrintMm(n) }) : '');
        var st = { draft: 'state_draft', edited: 'state_edited', saved: 'state_saved', away: 'state_away', cloud: 'state_cloud' }[s.state];
        el.state.setAttribute('data-state', s.state);
        setText(el.state, t(st));

        var away = s.state === 'away' || s.state === 'cloud';
        show(el.actPrint, !away);
        show(el.actEdit, !away);
        show(el.actDelete, !away);
        show(el.actAudio, !away && !!r.audio);
        show(el.actLink, true);
        show(el.actNfc, !away && 'NDEFReader' in window);
        disarmDelete();
        var note = '';
        if (s.state === 'draft') note = t('note_draft');
        else if (s.state === 'edited') note = t('note_edited');
        else if (s.state === 'cloud') note = t('note_cloud');
        else if (away) note = r.text ? t('note_away_text') : t('note_away');
        setText(el.sideNote, note);
        show(el.sideNote, !!note);
        renderExpiry(r.expires || '');
    }

    function renderExpiry(expires) {
        var info = C.expiryInfo(expires);
        setText(el.expV, info ? (info.state === 'expired' ? t('exp_v_expired', { date: fmtDate(info.date, { month: 'short' }) }) : t('exp_v', { date: fmtDate(info.date, { month: 'short' }), rel: relDays(info.days) })) : t('exp_none'));
        el.expDate.value = info ? info.date : '';
        var s = sideLabel();
        var editable = !!s && s.state !== 'away';
        el.expChips.querySelectorAll('.dk-chip').forEach(function (b) { b.disabled = !editable; });
        el.expDate.disabled = !editable;
    }

    function setExpiry(val) {
        var s = sideLabel();
        if (!s || s.state === 'away') return;
        val = C.normDate(val);
        if (S.mode === 'create') {
            S.draft.expires = val;
            if (S.draft.saved) {
                S.draft.dirty = true;
                saveDraft();
                return;
            }
            renderSide();
            return;
        }
        var row = S.byId[s.row.id];
        if (!row) return;
        row.expires = val;
        row.updatedAt = Date.now();
        Store.put(row).then(function () { return reload(); }).then(function () {
            cloudAfterChange();
            S.current = { row: S.byId[row.id], here: true };
            renderSide();
            if (S.draft && S.draft.editingId === row.id) S.draft.expires = val;
        });
        feat('expiry_set', { v: val ? 1 : 0 });
    }

    function setCurrent(row, here) {
        S.current = row ? { row: row, here: here !== false } : null;
        if (S.mode === 'library' || S.wide) renderList();
        renderSide();
    }

    /* ── sticker actions ─────────────────────────────────────────────────── */
    function currentRow() { var s = sideLabel(); return s ? s.row : null; }

    function disarmDelete() {
        if (!S.armedDelete) return;
        clearTimeout(S.armedDelete); S.armedDelete = 0;
        el.actDelete.classList.remove('is-armed');
    }
    function onDelete() {
        var r = currentRow();
        if (!r || !S.byId[r.id]) return;
        if (!S.armedDelete) {
            el.actDelete.classList.add('is-armed');
            announce(t('act_delete_confirm'));
            S.armedDelete = setTimeout(disarmDelete, 4000);
            return;
        }
        disarmDelete();
        var keep = S.byId[r.id];
        if (CL && CL.inCloud(r.id)) CL.remove(r.id);
        Store.remove(r.id).then(reload).then(function () {
            if (S.draft && S.draft.editingId === r.id) startNew();
            setCurrent(null);
            toast(t('deleted', { name: labelName(keep) }), 'success');
            announce(t('deleted', { name: labelName(keep) }));
            feat('label_deleted');
        });
    }
    function onLink() {
        var r = currentRow();
        if (!r) return;
        var url = urlOf(r);
        var done = function () { setText(el.sideNote, t('link_copied')); show(el.sideNote, true); toast(t('link_copied'), 'success'); };
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, function () { window.prompt(t('act_link'), url); });
            else window.prompt(t('act_link'), url);
        } catch (e) { window.prompt(t('act_link'), url); }
        feat('link_copy');
    }
    function onAudio() {
        var r = currentRow();
        if (!r || !r.audio) return;
        var ext = /mpeg|mp3/.test(r.mime) ? 'mp3' : (/mp4|aac/.test(r.mime) ? 'm4a' : (/ogg/.test(r.mime) ? 'ogg' : 'webm'));
        var blob = new Blob([r.audio], { type: r.mime || 'audio/mpeg' });
        if (window.__downloadFile) window.__downloadFile(blob, fileBase(r.text, 'voice-label') + '.' + ext);
        feat('audio_download');
    }

    /* ── NFC (Chrome on Android) ─────────────────────────────────────────── */
    var nfcWrite = null;
    function onNfc() {
        var r = currentRow();
        if (!r || !('NDEFReader' in window)) return;
        if (nfcWrite) { try { nfcWrite.abort(); } catch (e) { } nfcWrite = null; return; }
        var ctrl = new AbortController();
        nfcWrite = ctrl;
        setText(el.sideNote, t('nfc_hold'));
        show(el.sideNote, true);
        announce(t('nfc_hold'));
        var started = Date.now();
        try {
            var w = new window.NDEFReader();
            w.write({ records: [{ recordType: 'url', data: urlOf(r) }] }, { signal: ctrl.signal, overwrite: true }).then(function () {
                nfcWrite = null;
                setText(el.sideNote, t('nfc_done'));
                toast(t('nfc_done'), 'success');
                announce(t('nfc_done'));
                try { if (navigator.vibrate) navigator.vibrate([40, 60, 40]); } catch (e) { }
                feat('nfc_write', { ok: 1, ms: Date.now() - started });
            }, function (err) {
                nfcWrite = null;
                if (err && err.name === 'AbortError') { setText(el.sideNote, ''); show(el.sideNote, false); return; }
                var msg = t('nfc_fail', { err: (err && err.name === 'NotAllowedError') ? t('nfc_denied') : ((err && err.message) || '') });
                setText(el.sideNote, msg);
                feat('nfc_write', { ok: 0, err: String((err && err.name) || err).slice(0, 40) });
            });
        } catch (err) {
            nfcWrite = null;
            setText(el.sideNote, t('nfc_fail', { err: (err && err.message) || '' }));
        }
    }

    /* ════════════════════════════════════════════════════════════════════
       SCANNER
       ════════════════════════════════════════════════════════════════════ */
    var SC = { cam: null, stream: null, track: null, loop: 0, busy: false, state: {}, session: 0, scanning: false, starting: false,
        lastKey: '', lastSeen: 0, torchOn: false, torchCap: false, nfcCtrl: null, fx: null, tick: 0, foreignAt: 0, holdWake: false };

    function fxCtx() {
        if (SC.fx) return SC.fx;
        try { SC.fx = new (window.AudioContext || window.webkitAudioContext)(); } catch (e) { SC.fx = null; }
        return SC.fx;
    }
    function tone(freqs, dur, vol) {
        var ctx = SC.fx;
        if (!ctx) return;
        try {
            if (ctx.state === 'suspended') ctx.resume();
            var t0 = ctx.currentTime + 0.01;
            freqs.forEach(function (f, i) {
                var o = ctx.createOscillator(), g = ctx.createGain();
                o.type = 'sine'; o.frequency.value = f;
                var s = t0 + i * dur;
                g.gain.setValueAtTime(0.0001, s);
                g.gain.exponentialRampToValueAtTime(vol, s + 0.012);
                g.gain.exponentialRampToValueAtTime(0.0001, s + dur);
                o.connect(g); g.connect(ctx.destination);
                o.start(s); o.stop(s + dur + 0.02);
            });
        } catch (e) { }
    }
    function startTicks() {
        stopTicks();
        SC.tick = setInterval(function () {
            if (!SC.scanning || !S.opts.sound || player.el && !player.el.paused) return;
            try { if (window.speechSynthesis && window.speechSynthesis.speaking) return; } catch (e) { }
            if (Date.now() - SC.lastSeen < 2500) return;
            tone([1450], 0.035, 0.05);
        }, 1400);
    }
    function stopTicks() { if (SC.tick) { clearInterval(SC.tick); SC.tick = 0; } }

    function setScanUi(state) {
        el.root.setAttribute('data-scan', state === 'on' ? (SC.lastKey && Date.now() - SC.lastSeen < 2500 ? 'found' : 'on') : (state === 'starting' ? 'on' : 'off'));
        var pill = el.scanPill;
        var m = { off: ['', 'scan_off'], starting: ['wait', 'scan_starting'], on: ['run', 'scan_on'] }[state] || ['', 'scan_off'];
        if (m[0]) pill.setAttribute('data-state', m[0]); else pill.removeAttribute('data-state');
        setText(pill, t(m[1]));
        var on = state === 'on' || state === 'starting';
        el.scanMain.classList.remove('dk-go', 'dk-stop', 'dk-primary');
        var lbl;
        if (on) { el.scanMain.classList.add('dk-stop'); lbl = t('btn_scan_stop'); }
        else if (S.landing) { el.scanMain.classList.add('dk-primary'); lbl = t('btn_listen'); }
        else { el.scanMain.classList.add('dk-go'); lbl = t(SC.lastKey ? 'btn_scan_next' : 'btn_scan_start'); }
        setText(el.scanMainLbl, lbl);
        show(el.video, state === 'on');
        show(el.camIdle, state === 'off' && el.found.hidden);
        show(el.torch, state === 'on' && SC.torchCap);
    }

    function onScanMain() {
        if (S.landing) {
            var lp = S.landing;
            S.landing = null;
            unlockAudio();
            el.found.classList.remove('is-player');
            playLabel(lp.row, lp.parsed, { via: 'link' });
            setScanUi('off');
            return;
        }
        if (SC.scanning || SC.starting) { stopScan(); return; }
        startScan();
    }

    function startScan() {
        unlockAudio();
        fxCtx();
        startNfcRead();
        if (!window.CameraManager || !window.__qrDecode || !window.__qrDecode.isSupported()) {
            idleMessage(t('scan_unsupported'), true);
            feat('scan_unsupported');
            return;
        }
        SC.starting = true;
        var session = ++SC.session;
        idleMessage(t('scan_idle'), false);
        setScanUi('starting');
        try { window.__qrDecode.ready().catch(function () { }); } catch (e) { }
        var started = Date.now();
        if (!SC.cam) SC.cam = new window.CameraManager({ slug: SLUG });
        var size = window.__qrDecode.liveCameraSize();
        SC.cam.start({ facingMode: 'environment', width: size.width, height: size.height }).then(function (stream) {
            if (session !== SC.session || !SC.starting || document.hidden) { stopScan(); return; }
            SC.stream = stream;
            SC.track = stream.getVideoTracks()[0] || null;
            SC.torchCap = false;
            try { var caps = SC.track && SC.track.getCapabilities ? SC.track.getCapabilities() : {}; SC.torchCap = !!caps.torch; } catch (e) { }
            try { el.video.srcObject = stream; } catch (e) { }
            SC.starting = false;
            SC.scanning = true;
            setScanUi('on');
            SC.cam.show(el.video, stream, { anchor: el.scanMain }).then(function (ok) {
                if (ok && session === SC.session) feat('scan_frames', { ms: Date.now() - started });
            });
            startLoop();
            startTicks();
            holdWake(true);
            feat('scan_start', { ms: Date.now() - started });
            stage('scanning');
            announce(t('scan_on_said'));
        }).catch(function (err) {
            var said = !!(SC.cam && SC.cam.hasHelp && SC.cam.hasHelp());
            stopScan();
            if (!said) idleMessage(SC.cam && SC.cam.getErrorMessage ? SC.cam.getErrorMessage(err && err.code) : t('scan_no_camera'), true);
            feat('scan_cam_failed', { code: String((err && err.code) || 'unknown').slice(0, 30), door: said ? 1 : 0 });
        });
    }

    function startLoop() {
        SC.state = {};
        SC.busy = false;
        if (SC.loop) clearInterval(SC.loop);
        SC.loop = setInterval(function () {
            if (!SC.scanning || SC.busy) return;
            var cut = window.__qrDecode.liveFrame(el.video, { aim: el.aim, state: SC.state });
            if (!cut) return;
            SC.busy = true;
            window.__qrDecode.fromCanvasFast(cut.canvas, { formats: ['QR_CODE'] }).then(function (res) {
                SC.busy = false;
                if (SC.scanning) onResults(res || [], 'camera');
            }).catch(function (err) {
                SC.busy = false;
                if (err && err.code === 'ENGINE') { stopScan(); idleMessage(t('scan_unsupported'), true); }
            });
        }, 250);
    }

    function onResults(res, via) {
        var hit = null, foreign = false;
        for (var i = 0; i < res.length; i++) {
            var p = C.parseScan(res[i].text);
            if (p) { hit = p; break; }
            if (res[i].text) foreign = true;
        }
        if (hit) { onCode(hit, via); return; }
        if (foreign && Date.now() - SC.foreignAt > 6000) {
            SC.foreignAt = Date.now();
            showFoundMsg(t('scan_foreign'));
        }
    }

    function onCode(parsed, via) {
        var key = parsed.id || ('t:' + parsed.text);
        var now = Date.now();
        var same = key === SC.lastKey && now - SC.lastSeen < 2500;
        SC.lastSeen = now;
        if (same) return;
        SC.lastKey = key;
        if (!S.opts.cont && via !== 'photo') stopScan();
        el.root.setAttribute('data-scan', SC.scanning ? 'found' : 'off');
        if (S.opts.sound) tone([880, 1318], 0.09, 0.12);
        try { if (S.opts.vib && navigator.vibrate) navigator.vibrate(50); } catch (e) { }
        var row = parsed.id ? S.byId[parsed.id] : null;
        var go = row ? Promise.resolve(row) : (parsed.id ? Store.get(parsed.id).catch(function () { return null; }) : Promise.resolve(null));
        go.then(function (r) {
            /* Not in this browser: the owner may keep it in the cloud — then the
               voice plays here too, on any phone, without a copy moved. */
            if (r || !parsed.id || !CL) return r;
            return CL.fetchCard(parsed.id, 3500).then(function (card) { return card ? cloudRow(card) : null; });
        }).then(function (r) {
            showFound(r, parsed, false);
            setCurrent(r || pseudoRow(parsed), !!(r && !r.fromCloud));
            el.replay.disabled = false;
            setTimeout(function () { playLabel(r, parsed, { via: via }); }, S.opts.sound ? 220 : 0);
            feat('scan_hit', { via: via, here: r && !r.fromCloud ? 1 : 0, cloud: r && r.fromCloud ? 1 : 0, voice: r && (r.audio || r.voiceUrl) ? 1 : 0, text: parsed.text ? 1 : 0 });
            stage('label_played');
        });
    }
    function cloudRow(c) {
        return { id: c.id, text: c.text, lang: c.lang, expires: c.expires, audio: null, voiceUrl: c.voice || '', mime: c.mime || '',
            duration: c.duration || 0, createdAt: c.updated || Date.now(), fromCloud: true };
    }
    function pseudoRow(p) {
        return { id: p.id || '', text: p.text, lang: p.lang, expires: p.expires, audio: null, duration: 0, createdAt: Date.now() };
    }

    function showFound(row, parsed, player) {
        var text = row ? labelName(row) : (parsed.text || t('found_unnamed'));
        setText(el.foundT, text);
        var exp = C.expiryInfo(row ? row.expires : parsed.expires);
        var parts = [];
        if (row && (row.audio || row.voiceUrl)) parts.push('<span class="vl-exp">' + icon(row.fromCloud ? 'cloud' : 'volume') + escHtml(C.fmtDuration(row.duration)) + '</span>');
        else if (parsed.text) parts.push('<span class="vl-exp">' + icon('volume') + escHtml(t('found_tts')) + '</span>');
        if (exp) parts.push('<span class="vl-exp" data-state="' + exp.state + '">' + icon('calendar') + escHtml(exp.state === 'expired' ? t('exp_v_expired', { date: fmtDate(exp.date, { month: 'short' }) }) : t('exp_v', { date: fmtDate(exp.date, { month: 'short' }), rel: relDays(exp.days) })) + '</span>');
        el.foundM.innerHTML = parts.join('');
        var msg = '';
        if (!row && parsed.id) msg = parsed.text ? t('found_away_text') : t('found_away');
        if (row && row.fromCloud && !row.voiceUrl) msg = t('found_cloud_text');
        if (parsed.kind === 'legacy' && !row) msg = t('found_away');
        if (player) msg = msg ? msg + ' ' + t('found_tap') : t('found_tap');
        el.found.classList.toggle('is-player', !!player);
        show(el.found, true);
        showFoundMsg(msg);
        show(el.camIdle, false);
        announce(text + (msg ? '. ' + msg : ''));
    }
    function showFoundMsg(msg) {
        setText(el.foundMsg, msg || '');
        show(el.foundMsg, !!msg);
        if (msg && el.found.hidden && S.mode === 'scan') { setText(el.foundT, ''); el.foundM.innerHTML = ''; show(el.found, true); }
    }

    function stopScan() {
        SC.scanning = false;
        SC.starting = false;
        SC.session++;
        if (SC.loop) { clearInterval(SC.loop); SC.loop = 0; }
        stopTicks();
        if (SC.torchOn) setTorch(false);
        if (SC.cam) { try { SC.cam.stop(); } catch (e) { } }
        try { el.video.srcObject = null; } catch (e) { }
        SC.stream = null; SC.track = null;
        stopNfcRead();
        holdWake(false);
        setScanUi('off');
    }
    function idleMessage(msg, warn) {
        setText(el.camIdleText, msg);
        el.camIdleText.classList.toggle('warn', !!warn);
        show(el.found, false);
        show(el.camIdle, !SC.scanning);
    }

    function setTorch(on) {
        if (!SC.track || !SC.torchCap || typeof window.__trackConstraint !== 'function') return;
        window.__trackConstraint(SC.track, { torch: !!on }).then(function (r) {
            SC.torchOn = !!(r && r.ok && on);
            el.torch.classList.toggle('is-on', SC.torchOn);
            el.torch.setAttribute('aria-pressed', SC.torchOn ? 'true' : 'false');
            if (r && !r.ok && r.reason === 'refused') { SC.torchCap = false; show(el.torch, false); }
        });
        feat('torch', { on: on ? 1 : 0 });
    }

    function holdWake(on) {
        try {
            if (!window.__wakeLock) return;
            if (on && !SC.holdWake) { window.__wakeLock.hold('voice-labels-scan'); SC.holdWake = true; }
            else if (!on && SC.holdWake) { window.__wakeLock.releaseHold('voice-labels-scan'); SC.holdWake = false; }
        } catch (e) { }
    }

    /* NFC reading runs beside the camera where the browser has Web NFC. It
       must be asked inside the press (its permission needs the gesture). */
    function startNfcRead() {
        if (!('NDEFReader' in window) || SC.nfcCtrl) return;
        try {
            var ctrl = new AbortController();
            SC.nfcCtrl = ctrl;
            var r = new window.NDEFReader();
            r.onreading = function (ev) {
                try {
                    (ev.message.records || []).forEach(function (rec) {
                        if (rec.recordType !== 'url' && rec.recordType !== 'absolute-url' && rec.recordType !== 'text') return;
                        var s = new TextDecoder(rec.encoding || 'utf-8').decode(rec.data);
                        var p = C.parseScan(s);
                        if (p) onCode(p, 'nfc');
                    });
                } catch (e) { }
            };
            r.scan({ signal: ctrl.signal }).then(function () {
                el.nfcBadge.classList.add('on');
                show(el.nfcBadge, true);
                feat('nfc_read_on');
            }, function () { SC.nfcCtrl = null; });
        } catch (e) { SC.nfcCtrl = null; }
    }
    function stopNfcRead() {
        if (SC.nfcCtrl) { try { SC.nfcCtrl.abort(); } catch (e) { } SC.nfcCtrl = null; }
        if (el.nfcBadge) el.nfcBadge.classList.remove('on');
    }

    /* A photo of the sticker: the phone's own camera app needs no site
       permission, so this works after a refusal and in in-app browsers. */
    function onPhotoBtn() {
        unlockAudio();
        fxCtx();
        var z = el.photoZone;
        if (!z) return;
        var touch = window.matchMedia && window.matchMedia('(pointer:coarse)').matches;
        if (touch && typeof z.__uploaderOpenCamera === 'function') z.__uploaderOpenCamera('vl_photo');
        else if (typeof z.__uploaderOpen === 'function') z.__uploaderOpen();
        feat('photo_open');
    }
    function onPhoto(file) {
        if (!file || !window.__qrDecode) return;
        idleMessage(t('photo_reading'), false);
        window.__qrDecode.fromFile(file, { formats: ['QR_CODE'] }).then(function (res) {
            var hit = null;
            (res || []).some(function (r) { hit = C.parseScan(r.text); return !!hit; });
            if (hit) { SC.lastKey = ''; onCode(hit, 'photo'); }
            else idleMessage((res && res.length) ? t('scan_foreign') : t('photo_none'), true);
            feat('photo_read', { ok: hit ? 1 : 0, n: (res || []).length });
        }).catch(function (err) {
            idleMessage(t('photo_none'), true);
            feat('photo_read', { ok: 0, err: String((err && err.message) || err).slice(0, 60) });
        });
    }

    /* A sticker opened by the phone's own camera app: the page arrives with the
       label in its address. Sound needs a tap here (no gesture came with the
       page), so the label is shown big and the main button plays it. */
    function onLanding() {
        var p = C.parseScan(location.href);
        if (!p) return false;
        var id = p.id;
        var row = id ? S.byId[id] || null : null;
        S.landing = { row: row, parsed: p };
        setMode('scan', { keep: true });
        showFound(row, p, true);
        setCurrent(row || pseudoRow(p), !!row);
        SC.lastKey = p.id || ('t:' + p.text);
        setScanUi('off');
        if (!row && id && CL) {
            CL.fetchCard(id, 6000).then(function (card) {
                if (!card || !S.landing || S.landing.parsed !== p) return;
                var cr = cloudRow(card);
                S.landing.row = cr;
                showFound(cr, p, true);
                setCurrent(cr, false);
                feat('landing_cloud', { voice: card.voice ? 1 : 0 });
            });
        }
        feat('landing', { here: row ? 1 : 0, text: p.text ? 1 : 0, legacy: p.kind === 'legacy' ? 1 : 0 });
        stage('landing');
        /* On a phone the tool sits under the intro: bring the label and its
           button on screen — only as far as needed ('nearest'), never a jump. */
        try { el.scanMain.focus({ preventScroll: true }); } catch (e) { }
        requestAnimationFrame(function () { try { el.scanDock.scrollIntoView({ block: 'nearest' }); } catch (e) { } });
        return true;
    }

    /* ════════════════════════════════════════════════════════════════════
       BACKUP · STORAGE
       ════════════════════════════════════════════════════════════════════ */
    /* ── cloud (Premium shelf) ─────────────────────────────────────────── */
    function cloudAfterChange() {
        if (CL && CL.state.enabled && CL.state.pro) CL.sync().then(function () { return reload(); }).then(renderSide);
    }
    function cloudErr(code, st) {
        if (code === 'cap_reached') return t('cloud_err_cap', { cap: st.cap || CFG.cloudCap || 0 });
        if (code === 'premium_required') return t('cloud_err_premium');
        if (code === 'auth' || code === 'http_401') return t('cloud_err_auth');
        if (code === 'network' || code === 'timeout') return t('cloud_err_net');
        return t('cloud_err_other', { err: code });
    }
    function renderCloud() {
        if (!el.cloud) return;
        var st = CL ? CL.state : null;
        var pro = !!(st && st.pro), signed = !!(st && st.signedIn);
        var cards = st ? Object.keys(st.cards).length : 0;
        show(el.cloudSwitchWrap, pro);
        el.cloudOn.checked = !!(st && st.enabled);
        show(el.cloudNote, pro);
        show(el.cloudOffer, !pro);
        var msg = '', warn = false, good = false;
        if (pro) {
            if (st.busy) msg = t('cloud_status_busy', { n: st.queue.length });
            else if (st.lastError) { msg = cloudErr(st.lastError, st); warn = true; }
            else if (st.enabled) { msg = t('cloud_status_on', { used: st.used, cap: st.cap || CFG.cloudCap || 0 }); good = true; }
            else msg = t('cloud_status_off');
        } else if (signed && cards) {
            var kept = Object.keys(st.cards).map(function (k) { return st.cards[k].kept; }).filter(Boolean).sort()[0];
            msg = kept ? t('cloud_lapsed', { date: fmtDate(String(kept).slice(0, 10)) }) : t('cloud_err_premium');
            warn = true;
        }
        setText(el.cloudStatusT, msg);
        show(el.cloudStatus, !!msg);
        el.cloudStatus.classList.toggle('warn', warn);
        el.cloudStatus.classList.toggle('good', good);
        renderBackupSummary();
        show(el.cloudTag, false);
        renderSide();
    }

    var persistedState = null;
    function renderBackupSummary() {
        var n = S.labels.length;
        var inCloud = CL && CL.state.pro && CL.state.enabled ? CL.state.used : 0;
        setText(el.backupV, t('backup_v', { n: n }) + (inCloud ? ' · ' + t('backup_v_cloud', { c: inCloud }) : (persistedState === true ? ' · ' + t('backup_safe') : '')));
        var store = el.store, txt;
        store.classList.remove('good', 'warn');
        if (persistedState === true) { store.classList.add('good'); txt = t('store_safe'); }
        else if (n > 0) { store.classList.add('warn'); txt = t('store_risk'); }
        else txt = t('store_where');
        setText(el.storeT, txt);
        el.backupSave.disabled = n === 0;
        el.backupShare.disabled = n === 0;
    }
    function askPersist() {
        Store.persist().then(function (p) { persistedState = p; renderBackupSummary(); feat('persist', { ok: p === true ? 1 : (p === false ? 0 : -1) }); });
    }
    function backupBlob() {
        return Store.all().then(function (rows) {
            var doc = C.backupDocument(rows.map(Store.toBackupRow));
            return new Blob([JSON.stringify(doc)], { type: 'application/json' });
        });
    }
    function backupName() {
        var d = (window.__localYmd ? window.__localYmd() : C.ymd(new Date()));
        return 'voice-labels-' + d + '.json';
    }
    function onBackupSave() {
        backupBlob().then(function (b) {
            if (window.__downloadFile) return window.__downloadFile(b, backupName());
        }).then(function (ok) {
            if (ok !== false) { toast(t('backup_saved'), 'success'); feat('backup_save', { n: S.labels.length }); }
        }).catch(function (err) { handleError(err, 'vl_backup_save', t('store_failed')); });
    }
    function onBackupShare() {
        backupBlob().then(function (b) {
            var f = new File([b], backupName(), { type: 'application/json' });
            if (navigator.canShare && navigator.canShare({ files: [f] })) {
                return navigator.share({ files: [f], title: t('title') }).then(function () { feat('backup_share', { n: S.labels.length }); });
            }
            if (window.__downloadFile) return window.__downloadFile(b, backupName());
        }).catch(function (err) {
            if (err && err.name === 'AbortError') return;
            handleError(err, 'vl_backup_share', t('store_failed'));
        });
    }
    function onBackupFile(e) {
        var f = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!f) return;
        f.text().then(function (txt) {
            var rows = C.parseBackup(txt);
            if (!rows) { toast(t('import_bad'), 'error'); feat('backup_load', { ok: 0 }); return; }
            if (!rows.length) { toast(t('import_none'), 'warning'); return; }
            var m = C.merge(S.labels, rows);
            return Store.putMany(m.put.map(Store.fromBackupRow)).then(reload).then(function () {
                cloudAfterChange();
                var msg = t('import_done', { added: m.added, updated: m.updated });
                toast(msg, 'success');
                announce(msg);
                askPersist();
                feat('backup_load', { ok: 1, added: m.added, updated: m.updated });
            });
        }).catch(function (err) { handleError(err, 'vl_backup_load', t('import_bad')); });
    }

    /* ════════════════════════════════════════════════════════════════════
       PRINT — sheet PDF through the label-maker engine, PNG for label printers
       ════════════════════════════════════════════════════════════════════ */
    var PR = { ids: [], paper: 'plain', size: 'm', sheet: 'div-24', tape: '40x30', copies: 1, start: 1, text: true, busy: false };
    var SHEETS = ['div-24', 'div-21', 'div-12', 'div-40', 'avery-l7160', 'avery-l7163', 'avery-l7165', 'avery-l7161', 'round-40', 'round-60', 'avery-5160', 'avery-5163'];
    var PLAIN = { s: { w: 50, h: 25, sq: 25 }, m: { w: 70, h: 35, sq: 35 }, l: { w: 100, h: 50, sq: 55 } };
    /* Plain paper is the paper in the tray: Letter where Letter is the norm
       (an A4 page printed «100 %» on Letter loses its last row). */
    var LETTER = /-(US|CA|MX|PH|CL|CO|VE|GT|CR|PA|DO|PR|SV|NI|HN|BO)$/i.test((navigator.languages && navigator.languages[0]) || navigator.language || '');

    function printRows() { return PR.ids.map(function (id) { return S.byId[id]; }).filter(Boolean); }
    function openPrint(ids) {
        PR.ids = ids.filter(function (id) { return S.byId[id]; });
        if (!PR.ids.length) return;
        PR.copies = 1;
        el.prCopies.value = '1';
        var rows = printRows();
        PR.text = rows.some(function (r) { return !!r.text; });
        el.prText.checked = PR.text;
        el.prText.disabled = !PR.text;
        setText(el.prWhatT, rows.length === 1 ? t('pr_what_one', { name: labelName(rows[0]) }) : t('pr_what_many', { n: rows.length }));
        show(el.prCopiesWrap, rows.length === 1);
        fillSheetSelect();
        applyPaperUi();
        try { el.printDlg.showModal(); } catch (e) { el.printDlg.setAttribute('open', ''); }
        renderPrintPreview();
        feat('print_open', { n: rows.length });
    }
    function fillSheetSelect() {
        if (el.prSheet.options.length || !window.LabelSheets) return;
        SHEETS.forEach(function (id) {
            var f = window.LabelSheets.byId(id);
            if (!f) return;
            var o = document.createElement('option');
            o.value = id;
            var size = f.shape === 'circle' ? '⌀' + Math.round(f.w) : Math.round(f.w) + '×' + Math.round(f.h);
            var brand = /^avery-/.test(id) ? 'Avery ' + id.slice(6).toUpperCase() + ' · ' : '';
            o.textContent = brand + f.page.toUpperCase() + ' · ' + t('pr_per_sheet', { n: f.cols * f.rows }) + ' · ' + size + ' ' + t('mm');
            el.prSheet.appendChild(o);
        });
        el.prSheet.value = PR.sheet;
    }
    function applyPaperUi() {
        el.prPaper.querySelectorAll('[data-paper]').forEach(function (b) {
            var on = b.getAttribute('data-paper') === PR.paper;
            b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        show(el.prSizeWrap, PR.paper === 'plain');
        show(el.prSheetWrap, PR.paper === 'sheet');
        show(el.prThermalWrap, PR.paper === 'thermal');
        show(el.prStartWrap, PR.paper === 'sheet');
        el.prSize.querySelectorAll('[data-size]').forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-size') === PR.size ? 'true' : 'false'); });
        setText(el.prGoLbl, PR.paper === 'thermal' ? t('pr_png') : t('pr_pdf'));
        setText(el.prHint, t(PR.paper === 'plain' ? 'pr_hint_plain' : (PR.paper === 'sheet' ? 'pr_hint_sheet' : 'pr_hint_thermal')));
    }

    function currentFmt() {
        var LS = window.LabelSheets;
        if (!LS) return null;
        if (PR.paper === 'sheet') return LS.byId(PR.sheet) || LS.byId('div-24');
        if (PR.paper === 'thermal') {
            var p = PR.tape.split('x');
            return LS.custom({ page: 'a4', cols: 1, rows: 1, w: +p[0], h: +p[1] });
        }
        var sz = PLAIN[PR.size] || PLAIN.m;
        var w = PR.text ? sz.w : sz.sq, h = PR.text ? sz.h : sz.sq;
        var pageId = LETTER ? 'letter' : 'a4', pg = LS.page(pageId);
        var cols = Math.max(1, Math.floor((pg.w - 14) / (w + 4))), rows = Math.max(1, Math.floor((pg.h - 14) / (h + 4)));
        return LS.custom({ page: pageId, cols: cols, rows: rows, w: w, h: h });
    }

    /* What one label holds: the code, and the text beside or under it. */
    function elementsFor(fmt, withText) {
        var safe = 1.5, iw = fmt.w - safe * 2, ih = fmt.h - safe * 2;
        var circle = fmt.shape === 'circle';
        if (circle) {
            if (withText && fmt.w >= 50) {
                return [
                    { type: 'barcode', name: 'code', symbology: 'qrcode', showText: false, x: 0.24, y: 0.1, w: 0.52, h: 0.52 },
                    { type: 'text', name: 'text', x: 0.17, y: 0.64, w: 0.66, h: 0.2, sizeRel: 0.1, bold: true, align: 'center', valign: 'middle', font: 'noto', color: '#000000' },
                ];
            }
            return [{ type: 'barcode', name: 'code', symbology: 'qrcode', showText: false, x: 0.15, y: 0.15, w: 0.7, h: 0.7 }];
        }
        if (!withText) {
            var side = Math.min(iw, ih);
            return [{ type: 'barcode', name: 'code', symbology: 'qrcode', showText: false, x: (iw - side) / 2 / iw, y: (ih - side) / 2 / ih, w: side / iw, h: side / ih }];
        }
        if (iw >= ih * 1.3) {
            var q = ih / iw;
            return [
                { type: 'barcode', name: 'code', symbology: 'qrcode', showText: false, x: 0, y: 0, w: q, h: 1 },
                { type: 'text', name: 'text', x: q + 0.05, y: 0, w: 1 - q - 0.05, h: 1, sizeRel: 0.24, bold: true, align: 'left', valign: 'middle', font: 'noto', color: '#000000' },
            ];
        }
        var qs = Math.min(iw, ih * 0.7);
        return [
            { type: 'barcode', name: 'code', symbology: 'qrcode', showText: false, x: (iw - qs) / 2 / iw, y: 0, w: qs / iw, h: qs / ih },
            { type: 'text', name: 'text', x: 0, y: qs / ih + 0.03, w: 1, h: 1 - qs / ih - 0.03, sizeRel: 0.13, bold: true, align: 'center', valign: 'middle', font: 'noto', color: '#000000' },
        ];
    }
    var measure = null;
    function measurer() {
        if (measure) return measure;
        if (window.LabelRender && window.LabelRender.measurer) measure = window.LabelRender.measurer(function () { return FONT; });
        return measure;
    }
    function fontsReady() {
        try { if (document.fonts && document.fonts.load) return Promise.all([document.fonts.load("700 20px 'VL Noto Sans'")]).catch(function () { }); } catch (e) { }
        return Promise.resolve();
    }
    function buildCells(fmt) {
        var LL = window.LabelLayout;
        var rows = printRows();
        var withText = PR.text && rows.some(function (r) { return !!r.text; });
        var els = elementsFor(fmt, withText);
        var list = [];
        var textEl = els.filter(function (e) { return e.type === 'text'; })[0] || null;
        var innerW = fmt.w - 3;
        var mz = measurer();
        /* The largest type at which the longest WORD still fits one line:
           layout would otherwise keep the big size and cut «Гречка,» into
           «Гречка» / «,». */
        var elsFor = function (r) {
            if (!textEl || !mz || !r.text) return els;
            var longest = String(r.text).split(/\s+/).reduce(function (a, w) { return w.length > a.length ? w : a; }, '');
            var unit = mz(longest, { size: 1, bold: true, font: 'noto' });
            var boxW = textEl.w * innerW;
            var want = textEl.sizeRel * fmt.h;
            var cap = unit > 0 ? (boxW / unit) * 0.97 : want;
            if (cap >= want) return els;
            return els.map(function (e) { return e === textEl ? Object.assign({}, e, { sizeRel: null, size: Math.max(2.2, cap) }) : e; });
        };
        rows.forEach(function (r) {
            var n = rows.length === 1 ? Math.max(1, Math.min(200, PR.copies | 0)) : 1;
            for (var i = 0; i < n; i++) list.push(r);
        });
        var cells = [];
        if (PR.paper === 'sheet') for (var s = 1; s < PR.start; s++) cells.push(null);
        var minQr = Infinity, tiny = false;
        list.forEach(function (r) {
            var out = LL.layoutLabel({ w: fmt.w, h: fmt.h }, elsFor(r), {
                measure: measurer() || undefined,
                resolve: function (e) { return e.name === 'code' ? urlOf(r) : (r.text || ''); },
            });
            out.items.forEach(function (it) {
                if (it.kind === 'barcode') minQr = Math.min(minQr, it.box.w, it.box.h);
            });
            if (out.issues.some(function (x) { return x.code === 'text_tiny' || x.code === 'text_overflow'; })) tiny = true;
            cells.push({ items: out.items, row: r });
        });
        var worst = 0;
        list.forEach(function (r) { worst = Math.max(worst, C.minPrintMm(qrModules(urlOf(r)))); });
        return { cells: cells, count: list.length, minQr: isFinite(minQr) ? minQr : 0, needMm: worst, tiny: tiny };
    }

    var prevTimer = 0;
    function renderPrintPreview() {
        clearTimeout(prevTimer);
        prevTimer = setTimeout(function () {
            if (!window.LabelSheets || !window.LabelLayout || !window.LabelRender) { setText(el.prSum, t('pr_loading')); return; }
            fontsReady().then(function () {
                var fmt = currentFmt();
                var b = buildCells(fmt);
                var per = fmt.cols * fmt.rows;
                var pages = Math.max(1, Math.ceil(b.cells.length / per));
                try {
                    window.LabelRender.renderSheet(el.prPrev, fmt, b.cells, {
                        barcodeUrl: function (it) { return qrDataUrl(it.value); },
                        fontFamilyFor: function () { return FONT; },
                        ariaLabel: t('pr_title'),
                    });
                    if (PR.paper === 'thermal') {
                        var svg = el.prPrev.querySelector('svg');
                        if (svg) svg.setAttribute('viewBox', fmt.x0 + ' ' + fmt.y0 + ' ' + fmt.w + ' ' + fmt.h);
                    }
                } catch (e) { el.prPrev.textContent = ''; }
                if (PR.paper === 'thermal') setText(el.prSum, t('pr_sum_png', { n: b.count }));
                else setText(el.prSum, t('pr_sum', { n: b.count, p: pages }));
                var warn = '';
                if (b.minQr && b.needMm && b.minQr + 0.5 < b.needMm) warn = t('pr_small', { mm: b.needMm, got: Math.round(b.minQr) });
                else if (b.tiny) warn = t('pr_tiny');
                setText(el.prWarnT, warn);
                show(el.prWarn, !!warn);
            });
        }, 60);
    }

    function onPrintGo() {
        if (PR.busy) return;
        var rows = printRows();
        if (!rows.length) return;
        PR.busy = true;
        el.prGo.disabled = true;
        var lbl = el.prGoLbl.textContent;
        setText(el.prGoLbl, t('pr_building'));
        var job = PR.paper === 'thermal' ? buildPngs() : buildPdf();
        job.then(function (ok) {
            if (ok !== false) feat(PR.paper === 'thermal' ? 'png_export' : 'pdf_export', { n: rows.length, paper: PR.paper, size: PR.paper === 'sheet' ? PR.sheet : (PR.paper === 'thermal' ? PR.tape : PR.size), copies: PR.copies });
        }).catch(function (err) {
            handleError(err, 'vl_print', t('pr_failed'));
        }).then(function () {
            PR.busy = false;
            el.prGo.disabled = false;
            setText(el.prGoLbl, lbl);
        });
    }
    function buildPdf() {
        if (!window.LabelPdf) return Promise.reject(new Error('label_pdf_missing'));
        return fontsReady().then(function () {
            var fmt = currentFmt();
            var b = buildCells(fmt);
            return window.LabelPdf.build({
                fmt: fmt, cells: b.cells.map(function (c) { return c ? { items: c.items } : null; }),
                outline: PR.paper === 'plain', cutMarks: false, ruler: PR.paper !== 'thermal', rulerCaption: '50 ' + t('mm'),
            });
        }).then(function (res) {
            var rows = printRows();
            var name = fileBase(rows.length === 1 && rows[0].text ? rows[0].text : t('pr_file'), 'voice-labels');
            return window.__downloadFile ? window.__downloadFile(res.blob, name + '.pdf') : false;
        });
    }
    /* One PNG per label at 300 dpi, the label's own size: label-printer apps
       (Niimbot, Brother, Phomemo) take an image and print it 1:1 on the tape. */
    function buildPngs() {
        return fontsReady().then(function () {
            var fmt = currentFmt();
            var b = buildCells(fmt);
            var dpmm = 300 / 25.4;
            var jobs = b.cells.filter(Boolean).map(function (c) {
                var cv = document.createElement('canvas');
                cv.width = Math.round(fmt.w * dpmm); cv.height = Math.round(fmt.h * dpmm);
                var g = cv.getContext('2d');
                g.fillStyle = '#fff'; g.fillRect(0, 0, cv.width, cv.height);
                c.items.forEach(function (it) {
                    var x = it.box.x * dpmm, y = it.box.y * dpmm, w = it.box.w * dpmm, h = it.box.h * dpmm;
                    if (it.kind === 'barcode') {
                        var q = window.qrcode(0, 'M'); q.addData(it.value); q.make();
                        var n = q.getModuleCount(), side = Math.min(w, h), cell = Math.floor(side / n);
                        var ox = x + (w - cell * n) / 2, oy = y + (h - cell * n) / 2;
                        g.fillStyle = '#000';
                        for (var r = 0; r < n; r++) for (var k = 0; k < n; k++) if (q.isDark(r, k)) g.fillRect(ox + k * cell, oy + r * cell, cell, cell);
                    } else if (it.kind === 'text') {
                        var size = it.size * dpmm, lh = it.lineHeight * dpmm, block = it.lines.length * lh;
                        var top = it.valign === 'middle' ? y + (h - block) / 2 : y;
                        g.fillStyle = '#000';
                        g.font = (it.bold ? '700 ' : '400 ') + size + 'px ' + FONT;
                        g.textAlign = it.align === 'center' ? 'center' : (it.align === 'right' ? 'right' : 'left');
                        g.textBaseline = 'alphabetic';
                        var tx = it.align === 'center' ? x + w / 2 : (it.align === 'right' ? x + w : x);
                        it.lines.forEach(function (ln, i) { g.fillText(ln, tx, top + i * lh + size * (it.ascentEm || 0.8)); });
                    }
                });
                var name = fileBase(c.row.text, 'voice-label');
                return window.__canvasBlob(cv, 'image/png').then(function (blob) { return { blob: blob, name: name + '.png' }; });
            });
            return Promise.all(jobs);
        }).then(function (items) {
            if (items.length === 1) return window.__downloadFile ? window.__downloadFile(items[0].blob, items[0].name) : false;
            if (window.__batchDeliver && window.__batchDeliver.deliver) return window.__batchDeliver.deliver(items.map(function (i) { return { blob: i.blob, name: i.name }; }), { archiveName: 'voice-labels.zip', archiveOnly: true });
            return window.__downloadFile ? window.__downloadFile(items[0].blob, items[0].name) : false;
        });
    }

    /* ════════════════════════════════════════════════════════════════════
       BOOT
       ════════════════════════════════════════════════════════════════════ */
    function bindOptions() {
        var o = S.opts;
        o.cont = setting('cont', true) !== false;
        o.sound = setting('sound', true) !== false;
        o.vib = setting('vib', true) !== false;
        o.sayExp = setting('sayexp', true) !== false;
        o.autostop = setting('autostop', true) !== false;
        o.rate = Number(setting('rate', 1)) || 1;
        el.optCont.checked = o.cont; el.optSound.checked = o.sound; el.optVib.checked = o.vib; el.optExp.checked = o.sayExp; el.autoStop.checked = o.autostop;
        show(el.optVibWrap, typeof navigator.vibrate === 'function');
        var sync = function () {
            el.scanSound.setAttribute('aria-pressed', o.sound ? 'true' : 'false');
            el.scanSound.classList.toggle('is-on', o.sound);
            el.optSound.checked = o.sound;
            el.rate.querySelectorAll('[data-rate]').forEach(function (b) { b.setAttribute('aria-pressed', Number(b.getAttribute('data-rate')) === o.rate ? 'true' : 'false'); });
            var v = [];
            v.push(o.cont ? t('scan_v_cont') : t('scan_v_one'));
            v.push(o.sound ? t('scan_v_sound') : t('scan_v_quiet'));
            v.push(o.rate + '×');
            setText(el.scanV, v.join(' · '));
        };
        el.optCont.addEventListener('change', function () { o.cont = el.optCont.checked; saveSetting('cont', o.cont); sync(); });
        el.optSound.addEventListener('change', function () { o.sound = el.optSound.checked; saveSetting('sound', o.sound); sync(); });
        el.scanSound.addEventListener('click', function () { o.sound = !o.sound; saveSetting('sound', o.sound); sync(); fxCtx(); if (o.sound) tone([1450], 0.035, 0.06); });
        el.optVib.addEventListener('change', function () { o.vib = el.optVib.checked; saveSetting('vib', o.vib); });
        el.optExp.addEventListener('change', function () { o.sayExp = el.optExp.checked; saveSetting('sayexp', o.sayExp); });
        el.autoStop.addEventListener('change', function () { o.autostop = el.autoStop.checked; saveSetting('autostop', o.autostop); if (S.rec === 'recording') setHint(t(o.autostop ? 'rec_hint_on' : 'rec_hint_on_manual')); });
        el.rate.addEventListener('click', function (e) {
            var b = e.target.closest('[data-rate]');
            if (!b) return;
            o.rate = Number(b.getAttribute('data-rate')) || 1;
            saveSetting('rate', o.rate);
            sync();
            unlockAudio();
            speak(t('rate_sample'), LOC);
        });
        sync();
    }

    function bindPrint() {
        el.prClose.addEventListener('click', function () { try { el.printDlg.close(); } catch (e) { el.printDlg.removeAttribute('open'); } });
        el.printDlg.addEventListener('click', function (e) { if (e.target === el.printDlg) { try { el.printDlg.close(); } catch (x) { } } });
        el.prPaper.addEventListener('click', function (e) {
            var b = e.target.closest('[data-paper]');
            if (!b) return;
            PR.paper = b.getAttribute('data-paper');
            saveSetting('paper', PR.paper);
            applyPaperUi(); renderPrintPreview();
        });
        el.prSize.addEventListener('click', function (e) {
            var b = e.target.closest('[data-size]');
            if (!b) return;
            PR.size = b.getAttribute('data-size');
            saveSetting('size', PR.size);
            applyPaperUi(); renderPrintPreview();
        });
        el.prSheet.addEventListener('change', function () { PR.sheet = el.prSheet.value; saveSetting('sheet', PR.sheet); renderPrintPreview(); });
        el.prTape.addEventListener('change', function () { PR.tape = el.prTape.value; saveSetting('tape', PR.tape); renderPrintPreview(); });
        var num = function (inp, key, lo, hi) {
            var f = function () { var v = Math.max(lo, Math.min(hi, parseInt(inp.value, 10) || lo)); PR[key] = v; renderPrintPreview(); };
            inp.addEventListener('input', f); inp.addEventListener('change', function () { f(); inp.value = PR[key]; });
        };
        num(el.prCopies, 'copies', 1, 200);
        num(el.prStart, 'start', 1, 100);
        el.prText.addEventListener('change', function () { PR.text = el.prText.checked; renderPrintPreview(); });
        el.prGo.addEventListener('click', onPrintGo);
        PR.paper = setting('paper', 'plain'); if (!/^(plain|sheet|thermal)$/.test(PR.paper)) PR.paper = 'plain';
        PR.size = setting('size', 'm'); if (!PLAIN[PR.size]) PR.size = 'm';
        PR.sheet = setting('sheet', 'div-24'); if (SHEETS.indexOf(PR.sheet) < 0) PR.sheet = 'div-24';
        PR.tape = setting('tape', '40x30');
        el.prTape.value = PR.tape;
        if (el.prTape.value !== PR.tape) { PR.tape = '40x30'; el.prTape.value = PR.tape; }
        try { if (DK) DK.steppers(el.printDlg); } catch (e) { }
    }

    function bindHotkeys() {
        document.addEventListener('keydown', function (e) {
            if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
            if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
            if (el.printDlg.open) return;
            var k = (e.key || '').toLowerCase();
            if ((k === 'r' || k === 'к') && S.mode === 'create') { e.preventDefault(); onMain(); }
            else if ((k === 's' || k === 'ы') && S.mode === 'scan') { e.preventDefault(); onScanMain(); }
            else if ((k === 'p' || k === 'з') && S.mode !== 'library') {
                var s = sideLabel();
                if (s) { e.preventDefault(); unlockAudio(); playLabel(s.row.audio ? s.row : (S.byId[s.row.id] || null), s.row, { via: 'key' }); }
            }
        });
    }

    function boot() {
        C = window.VoiceLabelsCore; Store = window.VoiceLabelsStore; DK = window.DeviceKit;
        if (!C || !Store) return;
        var ids = {
            root: 'vlRoot', count: 'vlCount', count2: 'vlCount2', paneCreate: 'vlPaneCreate', paneScan: 'vlPaneScan', paneLib: 'vlPaneLibrary',
            recPill: 'vlRecPill', recTime: 'vlRecTime', recMax: 'vlRecMax', recHint: 'vlRecHint', autoStop: 'vlAutoStop', wave: 'vlWave',
            text: 'vlText', textCount: 'vlTextCount', recDock: 'vlRecDock', main: 'vlMain', mainLbl: 'vlMainLbl', play: 'vlPlay', playLbl: 'vlPlayLbl',
            redo: 'vlRedo', saveText: 'vlSaveText', cancelEdit: 'vlCancelEdit',
            video: 'vlVideo', aim: 'vlAim', scanPill: 'vlScanPill', torch: 'vlTorch', camIdle: 'vlCamIdle', camIdleText: 'vlCamIdleText',
            photoBtn: 'vlPhotoBtn', photoZone: 'vlPhotoZone', nfcBadge: 'vlNfcBadge', found: 'vlFound', foundT: 'vlFoundT', foundM: 'vlFoundM', foundMsg: 'vlFoundMsg',
            scanDock: 'vlScanDock', scanMain: 'vlScanMain', scanMainLbl: 'vlScanMainLbl', replay: 'vlReplay', scanSound: 'vlScanSound',
            search: 'vlSearch', pickMode: 'vlPickMode', printAll: 'vlPrintAll', printAllLbl: 'vlPrintAllLbl', list: 'vlList', empty: 'vlEmpty',
            emptyT: 'vlEmptyT', emptyP: 'vlEmptyP', emptyGo: 'vlEmptyGo', pickFoot: 'vlPickFoot', pickInfo: 'vlPickInfo', pickAll: 'vlPickAll', pickNone: 'vlPickNone',
            side: 'vlSide', state: 'vlState', stickerEmpty: 'vlStickerEmpty', sticker: 'vlSticker', qr: 'vlQr', stickerT: 'vlStickerT', stickerV: 'vlStickerV', stickerDur: 'vlStickerDur',
            code: 'vlCode', acts: 'vlActs', actPlay: 'vlActPlay', actPrint: 'vlActPrint', actNfc: 'vlActNfc', actLink: 'vlActLink', actEdit: 'vlActEdit',
            actAudio: 'vlActAudio', actDelete: 'vlActDelete', actDeleteLbl: 'vlActDeleteLbl', sideNote: 'vlSideNote',
            expV: 'vlExpV', expChips: 'vlExpChips', expDate: 'vlExpDate', scanV: 'vlScanV',
            optCont: 'vlOptCont', optSound: 'vlOptSound', optVib: 'vlOptVib', optVibWrap: 'vlOptVibWrap', optExp: 'vlOptExp', rate: 'vlRate',
            backupV: 'vlBackupV', store: 'vlStore', storeT: 'vlStoreT', backupSave: 'vlBackupSave', backupShare: 'vlBackupShare', backupLoad: 'vlBackupLoad', backupFile: 'vlBackupFile', iosNote: 'vlIosNote',
            printDlg: 'vlPrintDlg', prClose: 'vlPrintClose', prWhatT: 'vlPrWhatT', prCopiesWrap: 'vlPrCopiesWrap', prCopies: 'vlPrCopies', prPaper: 'vlPrPaper',
            prSizeWrap: 'vlPrSizeWrap', prSize: 'vlPrSize', prSheetWrap: 'vlPrSheetWrap', prSheet: 'vlPrSheet', prThermalWrap: 'vlPrThermalWrap', prTape: 'vlPrTape',
            prStartWrap: 'vlPrStartWrap', prStart: 'vlPrStart', prText: 'vlPrText', prWarn: 'vlPrWarn', prWarnT: 'vlPrWarnT', prPrev: 'vlPrSheetPrev', prSum: 'vlPrSum',
            prHint: 'vlPrHint', prGo: 'vlPrGo', prGoLbl: 'vlPrGoLbl',
            neuralWrap: 'vlNeuralWrap', neural: 'vlNeural', neuralPop: 'vlNeuralPop', neuralVoices: 'vlNeuralVoices', neuralGo: 'vlNeuralGo',
            neuralGoLbl: 'vlNeuralGoLbl', neuralMsg: 'vlNeuralMsg', neuralMsgT: 'vlNeuralMsgT',
            cloud: 'vlCloud', cloudTag: 'vlCloudTag', cloudSwitchWrap: 'vlCloudSwitchWrap', cloudOn: 'vlCloudOn', cloudStatus: 'vlCloudStatus',
            cloudStatusT: 'vlCloudStatusT', cloudNote: 'vlCloudNote', cloudOffer: 'vlCloudOffer', cloudGo: 'vlCloudGo',
        };
        Object.keys(ids).forEach(function (k) { el[k] = $(ids[k]); });
        el.tabs = Array.prototype.slice.call(el.root.querySelectorAll('.vl-modes [role=tab]'));
        setText(el.recMax, C.fmtDuration(MAX_S));
        el.text.setAttribute('maxlength', String(C.TEXT_MAX));

        S.draft = newDraft(null);
        bindOptions();
        bindPrint();
        bindHotkeys();

        /* modes */
        el.tabs.forEach(function (b, i) {
            b.addEventListener('click', function () { setMode(b.getAttribute('data-mode')); });
            b.addEventListener('keydown', function (e) {
                var k = e.key, n = el.tabs.length, j = -1;
                var rtl = document.documentElement.dir === 'rtl';
                if (k === 'ArrowRight') j = rtl ? (i - 1 + n) % n : (i + 1) % n;
                else if (k === 'ArrowLeft') j = rtl ? (i + 1) % n : (i - 1 + n) % n;
                else if (k === 'Home') j = 0;
                else if (k === 'End') j = n - 1;
                if (j < 0) return;
                e.preventDefault();
                el.tabs[j].focus();
                setMode(el.tabs[j].getAttribute('data-mode'));
            });
        });

        /* recorder */
        el.main.addEventListener('click', onMain);
        el.wave.addEventListener('click', function () {
            if (S.rec === 'idle' || S.rec === 'recording') onMain();
            else if (S.draft && S.draft.take) togglePreview();
        });
        el.play.addEventListener('click', togglePreview);
        el.redo.addEventListener('click', redo);
        el.saveText.addEventListener('click', saveDraft);
        el.cancelEdit.addEventListener('click', cancelEdit);
        el.text.addEventListener('input', onText);
        el.neuralPop.addEventListener('beforetoggle', function (e) { if (e.newState === 'open') renderNeuralVoices(); });
        el.neural.addEventListener('click', function () { if (!window.HTMLElement || !('popover' in HTMLElement.prototype)) renderNeuralVoices(); });
        el.neuralVoices.addEventListener('click', function (e) {
            var b = e.target.closest('[data-voice]');
            if (!b) return;
            el.neuralVoices.querySelectorAll('[data-voice]').forEach(function (x) { var on = x === b; x.setAttribute('aria-checked', on ? 'true' : 'false'); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        });
        el.neuralGo.addEventListener('click', onNeuralGo);
        el.text.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' && !e.isComposing) {
                e.preventDefault();
                var d = S.draft;
                if (d.take || C.cleanText(d.text)) { if (!d.saved || d.dirty) saveDraft(); }
            }
        });

        /* scanner */
        el.scanMain.addEventListener('click', onScanMain);
        el.found.addEventListener('click', function () { if (S.landing) onScanMain(); });
        el.replay.addEventListener('click', function () {
            var s = S.current && S.current.row;
            if (!s) return;
            unlockAudio();
            var row = S.byId[s.id] || (s.voiceUrl ? s : null);
            playLabel(row, row ? null : s, { via: 'replay' });
        });
        el.torch.addEventListener('click', function () { setTorch(!SC.torchOn); });
        el.photoBtn.addEventListener('click', onPhotoBtn);
        if (el.photoZone) {
            if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(el.photoZone, onPhoto);
            else el.photoZone.addEventListener('tool-uploader:file', function (e) { onPhoto(e.detail && e.detail.file); });
        }
        show(el.nfcBadge, 'NDEFReader' in window);
        document.addEventListener('visibilitychange', function () { if (document.hidden && (SC.scanning || SC.starting)) stopScan(); });
        window.addEventListener('pagehide', function () { if (SC.scanning || SC.starting) stopScan(); if (S.rec === 'recording') stopRec('pagehide'); });

        /* library */
        el.list.addEventListener('click', onListClick);
        el.search.addEventListener('input', function () { S.query = el.search.value; renderList(); });
        el.pickMode.addEventListener('click', function () { S.pick = !S.pick; if (!S.pick) S.picked = {}; renderList(); });
        el.pickAll.addEventListener('click', function () { S.labels.forEach(function (r) { S.picked[r.id] = true; }); renderList(); });
        el.pickNone.addEventListener('click', function () { S.picked = {}; renderList(); });
        el.printAll.addEventListener('click', function () {
            var ids2 = S.pick ? Object.keys(S.picked).filter(function (k) { return S.picked[k] && S.byId[k]; }) : S.labels.map(function (r) { return r.id; });
            openPrint(ids2);
        });
        el.emptyGo.addEventListener('click', function () { setMode('create'); try { el.main.focus({ preventScroll: true }); } catch (e) { } });

        /* sticker actions */
        el.actPlay.addEventListener('click', function () {
            var s = sideLabel();
            if (!s) return;
            unlockAudio();
            var row = S.byId[s.row.id] || (s.row.audio || s.row.voiceUrl ? s.row : null);
            playLabel(row, row ? null : s.row, { via: 'sticker' });
        });
        el.actPrint.addEventListener('click', function () { var r = currentRow(); if (r) openPrint([r.id]); });
        el.actLink.addEventListener('click', onLink);
        el.actEdit.addEventListener('click', function () { var r = currentRow(); if (r && S.byId[r.id]) editLabel(S.byId[r.id]); });
        el.actAudio.addEventListener('click', onAudio);
        el.actDelete.addEventListener('click', onDelete);
        el.actNfc.addEventListener('click', onNfc);

        /* expiry */
        el.expChips.addEventListener('click', function (e) {
            var b = e.target.closest('[data-exp]');
            if (!b) return;
            var k = b.getAttribute('data-exp'), today = new Date();
            var v = { d3: C.addDays(today, 3), w1: C.addDays(today, 7), m1: C.addMonths(today, 1), m3: C.addMonths(today, 3), m6: C.addMonths(today, 6), y1: C.addMonths(today, 12), none: '' }[k];
            setExpiry(v == null ? '' : v);
        });
        el.expDate.addEventListener('change', function () { setExpiry(el.expDate.value); });

        /* backup */
        el.backupSave.addEventListener('click', onBackupSave);
        el.backupShare.addEventListener('click', onBackupShare);
        el.backupLoad.addEventListener('click', function () { el.backupFile.click(); });
        el.backupFile.addEventListener('change', onBackupFile);
        try {
            var probe = new File(['{}'], 'x.json', { type: 'application/json' });
            show(el.backupShare, !!(navigator.canShare && navigator.canShare({ files: [probe] })));
        } catch (e) { show(el.backupShare, false); }
        var ios = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        show(el.iosNote, ios);

        try { if (DK) { DK.accordion(el.side, { slug: SLUG }); DK.fitDock(el.recDock); DK.fitDock(el.scanDock); } } catch (e) { }
        if (window.ResizeObserver) new ResizeObserver(function () { drawWave(); }).observe(el.wave);
        var checkWide = function () {
            var w = el.root.getBoundingClientRect().width >= 1320;
            if (w === S.wide) return;
            S.wide = w;
            if (w && S.mode === 'library') setMode('create', { keep: true });
            else renderList();
        };
        checkWide();
        if (window.ResizeObserver) new ResizeObserver(checkWide).observe(el.root);
        else window.addEventListener('resize', checkWide);

        Store.persisted().then(function (p) { persistedState = p; renderBackupSummary(); });

        /* Cloud: the account decides (session bootstrap → tier). Signed in —
           pull the owner's cloud labels into this browser; Premium with the
           switch on — push what differs. The switch is the person's choice
           (off by default, as the whiteboard shelf: storage by a button). */
        if (window.VoiceLabelsCloud) {
            CL = window.VoiceLabelsCloud.create({ store: Store, core: C, onChange: renderCloud });
            var tierSeen = '';
            var applyTier = function (pro) {
                var tl = window.__tierLimits;
                var signed = !!(tl && tl.tier_key && tl.tier_key !== 'anonymous');
                var key = signed + '/' + !!pro;
                if (key === tierSeen) return;
                tierSeen = key;
                CL.setAccount(signed, !!pro);
                CL.state.enabled = !!pro && setting('cloud', false) === true;
                if (!signed) { renderCloud(); return; }
                CL.pull().then(function (st) {
                    if (st.pulled) feat('cloud_pull', { n: st.pulled });
                    return reload();
                }).then(function () {
                    renderCloud();
                    if (CL.state.enabled) return CL.sync().then(reload).then(renderSide);
                });
            };
            if (DK && DK.onTier) DK.onTier(applyTier); else applyTier(false);
            el.cloudOn.addEventListener('change', function () {
                var on = el.cloudOn.checked;
                saveSetting('cloud', on);
                feat('cloud_on', { on: on ? 1 : 0 });
                if (on) askPersist();
                CL.setEnabled(on).then(function () { return reload(); }).then(renderSide);
            });
            el.cloudGo.addEventListener('click', function () { feat('cloud_offer_click', { signed: CL.state.signedIn ? 1 : 0 }); });
        }
        renderCloud();
        reload().then(function () {
            window.addEventListener('hashchange', onLanding);
            if (onLanding()) return;
            var m = setting('mode', 'create');
            if (m === 'library' && !S.labels.length) m = 'create';
            setMode(/^(create|scan|library)$/.test(m) ? m : 'create', { force: true, keep: true });
            setRec('idle');
        });
        setRec('idle');
        stage('ready');
        window.__VL_READY = true;
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
})();
